<template>
  <!-- eslint-disable vue/no-multiple-template-root,vue/no-parsing-error -->
  <div class="house-review-page" :class="[layoutClass, { 'is-guest': isGuest }]">
    <!-- Guest login CTA (sticky banner) -->
    <div v-if="isGuest" class="guest-login-cta">
      <div class="guest-login-cta-inner">
        <span class="guest-login-cta-icon">🔒</span>
        <span class="guest-login-cta-text">
          Log in to see full reviews, photos, address, and similar houses.
        </span>
        <button class="guest-login-cta-btn" @click="goLogin">Log in</button>
      </div>
    </div>

    <!-- 3D Background Elements (from MainPage) -->
    <div class="bg-3d-container">
      <div class="floating-orb orb-1"></div>
      <div class="floating-orb orb-2"></div>
      <div class="floating-orb orb-3"></div>
    </div>

    <!-- Image Overlay Modal -->
    <div v-if="showOverlay" class="image-overlay" @click.self="closeOverlay">
      <span class="close-button" @click="closeOverlay">&times;</span>
      <img :src="currentImage" class="enlarged-image" />
    </div>

    <!-- Hero Section -->
    <div class="hero-section">
      <div class="hero-container">
        <div class="hero-card glass-card">
          <!-- Left Column - Rating & Property Info -->
          <div class="hero-left">
            <!-- Rating & Verification -->
            <div class="rating-block">
              <div class="rating-summary">
                <div class="rating-main">
                  <span v-if="reviewInfos.length === 0" class="rating-big rating-not-reviewed">{{ $t('emptyState.notReviewedYet') }}</span>
                  <template v-else>
                    <span class="rating-big" :class="getRatingColorClass(allinfo.average)">{{ formatSafety(allinfo.average) }}</span>
                    <span class="rating-max">/ 5</span>
                  </template>
                </div>
                <p class="avg-rent-line">{{ averageMonthlyRentText }}</p>
              </div>
              <div class="mobile-right-stack">
                <div class="verification-badge" :class="{ verified: searchResults1 && searchResults1.verify }">
                  <div class="verification-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L14.5 8.5L21 9L16 14L17.5 20.5L12 17L6.5 20.5L8 14L3 9L9.5 8.5L12 2Z" :fill="searchResults1 && searchResults1.verify ? '#22c55e' : '#D1D5DB'"/>
                      <path d="M9 12L11 14L15 10" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <div class="verification-text">
                    <span class="verification-label">This place is</span>
                    <span class="verification-status">{{ searchResults1 && searchResults1.verify ? 'Verified' : 'Not Verified' }}</span>
                  </div>
                </div>
                <div class="mobile-price-block" v-if="mobilePriceInfo">
                  <span class="mobile-price-value">HK${{ mobilePriceInfo.value }}</span>
                  <span class="mobile-price-unit">{{ mobilePriceInfo.unit }}</span>
                </div>
                <!-- Room-level like button (mobile). Writes to room_like table, NOT favorites. -->
                <button
                  v-if="!isGuest"
                  class="heart-btn mobile-like-btn"
                  :class="{ active: roomLiked }"
                  @click="toggleRoomLike"
                  :title="roomLiked ? 'Unlike this room' : 'Like this room'"
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                      :fill="roomLiked ? '#ef4444' : 'none'"
                      :stroke="roomLiked ? '#ef4444' : '#64748b'"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"/>
                  </svg>
                  <span v-if="roomLikeCount > 0" class="like-count">{{ roomLikeCount }}</span>
                </button>
              </div>
            </div>
            <ReviewsCountLabel :count="reviewInfos.length" />

            <!-- Property Title -->
            <div class="property-title-block" :class="{ 'guest-blur': isGuest }">
              <h1 class="property-name">{{ (res && res.name) ? res.name : '' }}</h1>
              <p class="property-address" v-if="res && (res.street || res.district || res.region)">{{ [res.street, res.address, res.detailedArea, res.area, res.district, res.region].filter(Boolean).join(', ') }}</p>
            </div>
            <div class="managed-by" v-if="res && (res.managedByName || res.type)">
              <span class="managed-by-name">{{ res.managedByName || res.type }}</span>
              <span class="managed-by-type" v-if="res.managedByType">({{ res.managedByType }})</span>
            </div>

            <div v-if="isAdmin" class="admin-delete-block">
              <button class="admin-edit-btn" @click="openAdminEdit">Edit Address</button>
              <button class="admin-delete-btn" @click="confirmDeleteAddress">Delete Address</button>
            </div>

            <div v-if="isAdmin && adminEditOpen" class="admin-edit-panel">
              <h3 class="admin-edit-title">Edit Address</h3>
              <div class="admin-edit-grid">
                <label>Name<input v-model="adminEditHouse.name" /></label>
                <label>Type<input v-model="adminEditHouse.type" placeholder="Company/Landlord/University" /></label>
                <label>Location Type<input v-model="adminEditHouse.locationType" placeholder="On-Campus / Off-Campus" /></label>
                <label>Managed By Type<input v-model="adminEditHouse.managedByType" placeholder="Private Landlord / Agency / University" /></label>
                <label>Managed By Name<input v-model="adminEditHouse.managedByName" /></label>
                <label>Region<input v-model="adminEditHouse.region" /></label>
                <label>District<input v-model="adminEditHouse.district" /></label>
                <label>Area<input v-model="adminEditHouse.area" /></label>
                <label>Detailed Area<input v-model="adminEditHouse.detailedArea" /></label>
                <label>Street<input v-model="adminEditHouse.street" /></label>
                <label class="admin-edit-full">Address<input v-model="adminEditHouse.address" /></label>
              </div>
              <button class="admin-save-btn" :disabled="adminSaving" @click="saveAdminHouseEdit">
                {{ adminSaving ? 'Saving...' : 'Save Address' }}
              </button>

              <h3 class="admin-edit-title" style="margin-top:24px;">Rooms</h3>
              <div v-for="(room, idx) in adminEditRooms" :key="room.id" class="admin-room-row">
                <div class="admin-room-header">
                  <span class="admin-room-label">Room {{ idx + 1 }}: {{ room.houseLetName || room.houseLet || room.id }}</span>
                  <button class="admin-room-delete-btn" @click="confirmDeleteRoom(room)">Delete Room</button>
                </div>
                <div class="admin-edit-grid">
                  <label>Room Name<input v-model="room.houseLetName" /></label>
                  <label>Unit<input v-model="room.houseLet" /></label>
                  <label>Property Type<input v-model="room.propertyType" /></label>
                  <label>Block<input v-model="room.block" /></label>
                  <label>Floor<input v-model="room.floor" /></label>
                  <label>Room<input v-model="room.room" /></label>
                </div>
                <button class="admin-save-btn" :disabled="adminSaving" @click="saveAdminRoomEdit(room)">
                  Save Room
                </button>
              </div>
              <p v-if="adminEditRooms.length === 0" class="admin-no-rooms">No rooms found for this address.</p>
            </div>

            <!-- Mobile: What Students Say sits right under the price/header -->
            <div v-if="currentStudentReview" class="students-say-section mobile-students-say" :class="{ 'guest-blur': isGuest }">
              <h3 class="section-label">WHAT STUDENTS SAY:</h3>
              <div
                class="student-comment-card swipeable"
                @touchstart="handleReviewSwipeStart"
                @touchend="handleReviewSwipeEnd"
                @mousedown="handleReviewSwipeStart"
                @mouseup="handleReviewSwipeEnd"
                @mouseleave="swipeStartX = 0"
              >
                <div class="comment-header">
                  <img :src="currentStudentReview.avatar || '/avatar/1.png'" alt="Student" class="comment-avatar" />
                  <span class="comment-school">{{ currentStudentReview.school || 'HKU' }}</span>
                </div>
                <p class="comment-text">
                  <strong>{{ currentStudentReview.name || 'Anonymous' }}</strong> · {{ currentStudentReview.content || 'Great place to stay!' }}
                </p>
                <div class="comment-dots">
                  <span
                    v-for="n in totalStudentReviews"
                    :key="`mobile-${n}`"
                    class="dot"
                    :class="{ active: n - 1 === currentReviewIndex }"
                    @click="goToReview(n - 1)"
                  ></span>
                </div>
              </div>
            </div>

            <!-- Action Buttons Row -->
            <div v-if="!isGuest" class="action-buttons-row">
              <!-- Room-level like button (desktop). Writes to room_like table, NOT favorites. -->
              <button class="heart-btn desktop-like-btn" :class="{ active: roomLiked }" @click="toggleRoomLike" :title="roomLiked ? 'Unlike this room' : 'Like this room'">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                    :fill="roomLiked ? '#ef4444' : 'none'"
                    :stroke="roomLiked ? '#ef4444' : '#64748b'"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"/>
                </svg>
                <span v-if="roomLikeCount > 0" class="like-count">{{ roomLikeCount }}</span>
              </button>

              <!-- Rate Button -->
              <RateThisPlaceButton @click="ToRegistInfo" />
            </div>

            <!-- Room Selector -->
            <div v-if="!isGuest" class="room-selector-section">
              <el-select 
                v-model="selectedRoom" 
                placeholder="Select Room" 
                @change="handleSelectChange2" 
                class="room-selector-styled"
              >
                <el-option
                  key="all-rooms"
                  label="All Rooms"
                  :value="0">
                </el-option>
                <el-option
                  v-for="item in (searchResults2 || [])"
                  :key="item.id"
                  :label="item.houseLetName"
                  :value="item.id">
                </el-option>
              </el-select>
              <el-button type="primary" size="small" @click="ToAddHouseRoom" class="add-room-btn">
                Add a New Room
              </el-button>
            </div>
          </div>

          <!-- Middle Column - Stats & Student Comments -->
          <div class="hero-middle">
            <!-- Rating Stats Card (Collapsible) -->
            <div class="stats-card glass-card">
              <div v-if="reviewInfos.length === 0" class="stats-empty">
                {{ $t('emptyState.notReviewedYet') }}
              </div>
              <template v-else>
              <!-- Always visible: Top Rated & Lowest Rated -->
              <div class="stats-summary">
                <!-- Top Rated -->
                <div class="stat-block top">
                  <div class="stat-block-header">
                    <span class="stat-block-label">Top Rated</span>
                    <span class="stat-block-score excellent">{{ formatSafety(allinfo[topCategoryKey]) }}</span>
                  </div>
                  <div class="stat-block-category">
                    <div class="stat-block-icon">
                      <svg v-if="topCategoryKey === 'safety'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      <svg v-else-if="topCategoryKey === 'costTransparency'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                      <svg v-else-if="topCategoryKey === 'location'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      <svg v-else-if="topCategoryKey === 'listingAccuracy'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                      <svg v-else-if="topCategoryKey === 'cleanliness'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                      <svg v-else-if="topCategoryKey === 'internetReliability'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1"/></svg>
                      <svg v-else-if="topCategoryKey === 'noiseLevel'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                      <svg v-else-if="topCategoryKey === 'roomSize'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                      <svg v-else-if="topCategoryKey === 'maintenance'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                      <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    </div>
                    <span class="stat-block-name">{{ topCategoryLabel }}</span>
                  </div>
                </div>

                <!-- Lowest Rated -->
                <div class="stat-block low">
                  <div class="stat-block-header">
                    <span class="stat-block-label">Lowest Rated</span>
                    <span class="stat-block-score warning">{{ formatSafety(allinfo[lowestCategoryKey]) }}</span>
                  </div>
                  <div class="stat-block-category">
                    <div class="stat-block-icon">
                      <svg v-if="lowestCategoryKey === 'safety'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      <svg v-else-if="lowestCategoryKey === 'costTransparency'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                      <svg v-else-if="lowestCategoryKey === 'location'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      <svg v-else-if="lowestCategoryKey === 'listingAccuracy'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                      <svg v-else-if="lowestCategoryKey === 'cleanliness'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                      <svg v-else-if="lowestCategoryKey === 'internetReliability'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1"/></svg>
                      <svg v-else-if="lowestCategoryKey === 'noiseLevel'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                      <svg v-else-if="lowestCategoryKey === 'roomSize'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                      <svg v-else-if="lowestCategoryKey === 'maintenance'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                      <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    </div>
                    <span class="stat-block-name">{{ lowestCategoryLabel }}</span>
                  </div>
                </div>
              </div>

              <!-- Expandable: All ratings -->
              <div v-if="showAllRatings" class="stats-expanded">
                <div class="stats-grid">
                  <div class="stat-row" v-for="cat in ratingCategoryList" :key="cat.key">
                    <div class="stat-icon">
                      <svg v-if="cat.key === 'safety'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                      <svg v-else-if="cat.key === 'costTransparency'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                      <svg v-else-if="cat.key === 'location'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                      </svg>
                      <svg v-else-if="cat.key === 'listingAccuracy'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                      </svg>
                      <svg v-else-if="cat.key === 'cleanliness'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
                      </svg>
                      <svg v-else-if="cat.key === 'internetReliability'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1"/>
                      </svg>
                      <svg v-else-if="cat.key === 'noiseLevel'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
                      </svg>
                      <svg v-else-if="cat.key === 'roomSize'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
                      </svg>
                      <svg v-else-if="cat.key === 'maintenance'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                      </svg>
                      <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                    </div>
                    <span class="stat-label">{{ getCategoryLabel(cat.key) }}</span>
                    <span class="stat-value" :class="getScoreClass(cat.value)">{{ formatSafety(cat.value) }}</span>
                  </div>
                </div>
              </div>

              <!-- Show All/Less Toggle -->
              <button v-if="!isGuest" class="show-all-btn" @click="showAllRatings = !showAllRatings">
                {{ showAllRatings ? 'Show Less' : 'Show All Ratings' }}
                <svg :class="{ rotated: showAllRatings }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              </template>
            </div>

            <!-- What Students Say -->
            <div v-if="currentStudentReview" class="students-say-section desktop-students-say" :class="{ 'guest-blur': isGuest }">
              <h3 class="section-label">WHAT STUDENTS SAY:</h3>
              <div 
                class="student-comment-card swipeable"
                @touchstart="handleReviewSwipeStart"
                @touchend="handleReviewSwipeEnd"
                @mousedown="handleReviewSwipeStart"
                @mouseup="handleReviewSwipeEnd"
                @mouseleave="swipeStartX = 0"
              >
                <div class="comment-header">
                  <img :src="currentStudentReview.avatar || '/avatar/1.png'" alt="Student" class="comment-avatar" />
                  <span class="comment-school">{{ currentStudentReview.school || 'HKU' }}</span>
                </div>
                <p class="comment-text">
                  <strong>{{ currentStudentReview.name || 'Anonymous' }}</strong> · {{ currentStudentReview.content || 'Great place to stay!' }}
                </p>
                <div class="comment-dots">
                  <span 
                    v-for="n in totalStudentReviews" 
                    :key="n" 
                    class="dot" 
                    :class="{ active: n - 1 === currentReviewIndex }"
                    @click="goToReview(n - 1)"
                  ></span>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column - Photos & Attributes -->
          <div class="hero-right">
            <!-- Photo Carousel -->
            <div class="photo-carousel">
              <div 
                class="photo-main swipeable"
                @click="!isSwiping && currentPhoto ? openOverlay(currentPhoto) : null"
                @touchstart="handlePhotoSwipeStart"
                @touchend="handlePhotoSwipeEnd"
                @mousedown="handlePhotoSwipeStart"
                @mouseup="handlePhotoSwipeEnd"
                @mouseleave="swipeStartX = 0; isSwiping = false"
              >
                <img 
                  v-if="currentPhoto" 
                  :src="currentPhoto" 
                  alt="Property" 
                  class="main-image"
                  @error="handleImageError"
                />
                <div v-else class="photo-placeholder">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                  <span>No photos yet</span>
                </div>
              </div>
              <div class="photo-dots" v-if="!isGuest && allShowimages.length">
                <span 
                  v-for="n in Math.min(allShowimages.length, 5)" 
                  :key="n" 
                  class="dot" 
                  :class="{ active: n - 1 === currentImageIndex }"
                  @click="goToImage(n - 1)"
                ></span>
              </div>
            </div>

            <div v-if="!isGuest" class="mobile-cta-block">
              <ReviewsCountLabel :count="reviewInfos.length" position="mobile-reviews-count" />
              <RateThisPlaceButton position="mobile-rate-btn" @click="ToRegistInfo" />
            </div>

            <!-- Property Attributes -->
            <div class="attributes-grid">
              <div class="attr-item">
                <span class="attr-label">Location Type</span>
                <span class="attr-value pill">{{ (res && res.locationType) || ((searchResults1 && searchResults1.propertyType) === 'Dorm' ? 'On-Campus' : 'Off-Campus') }}</span>
              </div>
              <div class="attr-item">
                <span class="attr-label">Property Type</span>
                <span class="attr-value">{{ (searchResults1 && searchResults1.propertyType) || (res && res.propertyType) || 'N/A' }}</span>
              </div>
              <div class="attr-item">
                <span class="attr-label">Room Type</span>
                <span class="attr-value">{{ mostCommonRoomType || 'N/A' }}</span>
              </div>
              <div class="attr-item">
                <span class="attr-label">Avg. Stay</span>
                <span class="attr-value">{{ averageStayDuration }}</span>
              </div>
              <div class="attr-item">
                <span class="attr-label">Shared Bathroom</span>
                <span class="attr-value">{{ sharedBathroomSummary }}</span>
              </div>
              <div class="attr-item">
                <span class="attr-label">Bed Type</span>
                <span class="attr-value">{{ mostCommonBedType || 'N/A' }}</span>
              </div>
            </div>

            <!-- Company Info or Copy Review -->
            <div class="review-actions-bar">
              <p v-if="res && (res.managedByName || res.type)" class="company-link">Managed by {{ res.managedByName || res.type }}<span v-if="res.managedByType"> ({{ res.managedByType }})</span></p>
              <button class="copy-info-btn" @click="copyReviewInfo" title="Copy review link to clipboard">
                📋 Copy Info
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Middle Section: Address + Tags + Distribution -->
    <!-- Reviews Section - Wrapper -->
    <div class="content-sections-wrapper">
      <div class="content-section">
        <div class="content-container">
        <div class="two-column-layout">
          <!-- Left: Address & Map -->
          <div class="address-card glass-card" :class="{ 'guest-blur': isGuest }">
            <div class="address-header">
              <h3 class="address-title">Address</h3>
              <button class="go-now-btn" @click="toBaiduHTTP()">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2Z"/>
                  <circle cx="12" cy="9" r="2.5" fill="white"/>
                </svg>
                Go Now
              </button>
            </div>
            <p class="address-text">{{ res ? [res.street, res.district, res.region].filter(Boolean).join(', ') : '' }}</p>
            <p class="address-detail" v-if="addressDetailLine">{{ addressDetailLine }}</p>
            
            <!-- Map -->
            <div class="map-container">
              <div ref="mapEl" class="map-view"></div>
              <div class="map-empty" v-if="!hasMapLocation">📍 No pinned location yet</div>
            </div>
          </div>

          <!-- Right: Tags + Distribution + Similar -->
          <div class="right-column">
            <!-- Top Tags -->
            <div class="tags-card glass-card" :class="{ 'guest-blur': isGuest }">
              <h3>Top Tags</h3>
              <div class="tags-container">
                <template v-if="topTagsList.length">
                  <span class="tag" :class="tagColorClass(index)" v-for="(tag, index) in topTagsList" :key="tag.name">{{ tag.name }} ({{ tag.count }})</span>
                </template>
                <span v-else class="tag">No tags yet</span>
              </div>
            </div>

            <!-- Rating Distribution -->
            <div class="distribution-card glass-card" :class="{ 'guest-blur': isGuest }">
              <h3>Rating Distribution</h3>
              <div v-if="totalReviews === 0" class="distribution-empty">
                {{ $t('emptyState.notReviewedYet') }}
              </div>
              <div v-else class="distribution-bars">
                <div class="dist-row">
                  <span class="dist-label">Amazing 5</span>
                  <div class="dist-bar"><div class="dist-fill" :style="{ width: amazingPercentage + '%' }"></div></div>
                  <span class="dist-count">{{ amazingCount }}</span>
                </div>
                <div class="dist-row">
                  <span class="dist-label">Good 4</span>
                  <div class="dist-bar"><div class="dist-fill" :style="{ width: goodPercentage + '%' }"></div></div>
                  <span class="dist-count">{{ goodCount }}</span>
                </div>
                <div class="dist-row">
                  <span class="dist-label">Average 3</span>
                  <div class="dist-bar"><div class="dist-fill" :style="{ width: averagePercentage + '%' }"></div></div>
                  <span class="dist-count">{{ averageCount }}</span>
                </div>
                <div class="dist-row">
                  <span class="dist-label">Poor 2</span>
                  <div class="dist-bar"><div class="dist-fill" :style="{ width: poorPercentage + '%' }"></div></div>
                  <span class="dist-count">{{ poorCount }}</span>
                </div>
                <div class="dist-row">
                  <span class="dist-label">Awful 1</span>
                  <div class="dist-bar"><div class="dist-fill" :style="{ width: awfulPercentage + '%' }"></div></div>
                  <span class="dist-count">{{ awfulCount }}</span>
                </div>
              </div>
            </div>

            <!-- Similar Houses -->
            <div class="similar-card glass-card" :class="{ 'guest-blur': isGuest }" v-if="similarHouses.length > 0">
              <h3>Similar Houses Nearby</h3>
              <div class="similar-list">
                <div
                  class="similar-item"
                  v-for="house in similarHouses"
                  :key="house.id"
                  @click="goToHouse(house.id)"
                  style="cursor:pointer"
                >
                  <span class="similar-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11.5L12 4l9 7.5"/><path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9"/></svg>
                  </span>
                  <span class="similar-name">{{ house.name }}</span>
                  <span class="similar-meta" v-if="house.avg || house.reviewCount">
                    <span v-if="house.avg" class="similar-rating-text">★ {{ house.avg.toFixed(1) }}</span>
                    <span v-if="house.reviewCount" class="similar-review-count">({{ house.reviewCount }})</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Reviews Section -->
    <div class="reviews-section" :class="{ 'guest-blur': isGuest }">
      <div class="content-container">
        <div class="reviews-header">
          <h2>{{ $t('reviews.title', { count: displayedReviews.length }) }}</h2>
          <div class="reviews-header-controls">
            <div class="review-search-bar">
              <svg class="review-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                v-model="reviewSearchQuery"
                type="text"
                class="review-search-input"
                :placeholder="$t('reviews.searchPlaceholder')"
              />
              <button v-if="reviewSearchQuery" class="review-search-clear" @click="reviewSearchQuery = ''">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2.5" stroke-linecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div class="room-filter" @click="toggleReviewFilter">
              <span class="filter-label">{{ reviewFilter === 'all' ? $t('reviews.allRooms') : (searchQuery2 || currentRoomLabel || $t('reviews.selectedRoom')) }}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </div>
          </div>
        </div>
        <div class="review-sort-filters">
          <button type="button" :class="['sort-pill', { active: reviewSortBy === 'latest' }]" @click.stop.prevent="setReviewSort('latest')">🕐 {{ $t('reviews.latest') }}</button>
          <button type="button" :class="['sort-pill', { active: reviewSortBy === 'earliest' }]" @click.stop.prevent="setReviewSort('earliest')">📅 {{ $t('reviews.earliest') }}</button>
          <button type="button" :class="['sort-pill', { active: reviewSortBy === 'rating-high' }]" @click.stop.prevent="setReviewSort('rating-high')">⭐ {{ $t('reviews.highestRated') }}</button>
          <button type="button" :class="['sort-pill', { active: reviewSortBy === 'rating-low' }]" @click.stop.prevent="setReviewSort('rating-low')">⭐ {{ $t('reviews.lowestRated') }}</button>
          <button type="button" :class="['sort-pill', { active: reviewSortBy === 'price-high' }]" @click.stop.prevent="setReviewSort('price-high')">💰 {{ $t('reviews.priceHighLow') }}</button>
          <button type="button" :class="['sort-pill', { active: reviewSortBy === 'price-low' }]" @click.stop.prevent="setReviewSort('price-low')">💰 {{ $t('reviews.priceLowHigh') }}</button>
        </div>
        <p v-if="reviewSearchQuery && displayedReviews.length === 0" class="no-search-results">
          {{ $t('reviews.noSearchResults', { query: reviewSearchQuery }) }}
        </p>

        <div class="reviews-list">
          <div v-for="(review, index) in displayedReviews" :key="review.id || index" class="review-card glass-card">
            <div class="review-header">
              <div class="review-rating" :class="getScoreClass(review.average)">
                {{ formatSafety(review.average) }}
              </div>
              <div class="reviewer-info">
                <img :src="review.avatar || '/avatar/1.png'" alt="Reviewer" class="reviewer-avatar" />
                <div class="reviewer-details">
                  <span class="reviewer-name">{{ review.name || $t('reviews.anonymous') }}</span>
                  <span class="reviewer-school" v-if="review.school">({{ review.school }})</span>
                  <span class="review-date">{{ formatDate(review.dateTime) }}</span>
                </div>
              </div>
              <div class="review-actions-top">
                <button v-if="review.ifSelf==1" class="action-btn edit" @click="editDetails(review.id)">{{ $t('reviews.edit') }}</button>
                <button v-if="canDeleteReview(review)" class="action-btn delete" @click="confirmDelete(review.id)">{{ $t('reviews.delete') }}</button>
                <button class="action-btn view" @click="viewDetails(review.id)">{{ $t('reviews.viewDetails') }}</button>
              </div>
            </div>

            <div class="review-badges" v-if="review.ifSelf==1 || review.recommend || review._roomName">
              <span v-if="review._roomName && reviewFilter === 'all'" class="badge room-badge">{{ review._roomName }}</span>
              <span v-if="review.ifSelf==1" class="badge yourself">{{ $t('reviews.yourself') }}</span>
              <span v-if="review.recommend" class="badge recommended" @click="viewDetails(review.id)">👍 {{ $t('reviews.recommended') }}</span>
            </div>

            <!-- Review Details Strip -->
            <div class="review-details-strip">
              <div class="detail-chip" v-if="review.roomType">
                <span class="detail-icon">🏠</span>
                <span class="detail-value">{{ review.roomType }}</span>
              </div>
              <div class="detail-chip" v-if="review.monthlyRent">
                <span class="detail-icon">💰</span>
                <span class="detail-value">HK${{ Number(review.monthlyRent).toLocaleString() }}/mo</span>
              </div>
              <div class="detail-chip" v-if="review.deposit">
                <span class="detail-icon">🔑</span>
                <span class="detail-value">HK${{ Number(review.deposit).toLocaleString() }} {{ $t('reviews.deposit') }}</span>
              </div>
              <div class="detail-chip" v-if="review.stayDuration">
                <span class="detail-icon">📅</span>
                <span class="detail-value">{{ getStayDurationLabel(review.stayDuration) }}</span>
              </div>
              <div class="detail-chip" v-if="review.roommatesNum != null && review.roommatesNum > 0">
                <span class="detail-icon">👥</span>
                <span class="detail-value">{{ review.roommatesNum }} {{ $t(review.roommatesNum != 1 ? 'reviews.flatmates' : 'reviews.flatmate') }}</span>
              </div>
              <div class="detail-chip" v-if="review.billsIncluded">
                <span class="detail-icon">📋</span>
                <span class="detail-value">{{ $t('reviews.billsIncluded') }}</span>
              </div>
              <div class="detail-chip" v-if="review.bedType">
                <span class="detail-icon">🛏️</span>
                <span class="detail-value">{{ review.bedType }}</span>
              </div>
              <div class="detail-chip" v-if="review.isShared != null">
                <span class="detail-icon">🚿</span>
                <span class="detail-value">{{ review.isShared ? $t('reviews.sharedBath') : $t('reviews.privateBath') }}</span>
              </div>
            </div>

            <!-- Biggest Problem -->
            <div v-if="review.biggestProblem" class="review-insight">
              <span class="insight-label">⚠️ {{ $t('reviews.biggestIssue') }}</span>
              <span class="insight-text">{{ review.biggestProblem }}</span>
            </div>

            <div class="translated-text-block">
              <p class="review-text">{{ getTranslatedReviewContent(review) }}</p>
              <button
                v-if="canTranslateText(review.content)"
                class="translation-toggle"
                type="button"
                :disabled="review._translationLoading"
                @click="toggleReviewTranslation(review)"
              >
                {{ getTranslationToggleLabel(review) }}
              </button>
            </div>

            <!-- Tags -->
            <div v-if="getValidTags(review.tags || review.email).length > 0" class="review-tags">
              <span class="review-tag" v-for="tag in getValidTags(review.tags || review.email)" :key="tag">{{ tag }}</span>
            </div>

            <!-- Review Images -->
            <div v-if="review.file && review.file.length > 0" class="review-images">
              <img 
                v-for="(image, imgIndex) in getimages(review.file)" 
                :key="imgIndex" 
                :src="image" 
                @click="openImageModal(image)"
                class="review-image"
                alt="Review image"
              />
            </div>

            <!-- Review Actions -->
            <div class="review-footer">
              <div class="helpful-actions">
                <span class="helpful-label">{{ $t('reviews.helpful') }}</span>
                <button class="helpful-btn" @click="prehandlelike(review)">👍 {{ review.liked }}</button>
                <button class="helpful-btn" @click="prehandleunlike(review)">👎 {{ review.unliked }}</button>
              </div>
              <button class="comment-toggle" @click="toggleComment(review)">
                <span>{{ (review.comments || []).length }}</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3CC2FF" stroke-width="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </button>
            </div>

            <!-- Comments Section -->
            <div v-if="review._showComments" class="comments-section">
              <h4>{{ $t('reviews.comments') }}</h4>
              <div v-if="review.comments && review.comments.length" class="comments-list">
                <div v-for="(comment, cIndex) in review.comments" :key="cIndex" class="comment-item">
                  <img :src="getCommentAvatar(comment)" alt="Commenter" class="comment-avatar-small" />
                  <div class="comment-content">
                    <div class="comment-meta">
                      <span class="comment-name">{{ comment.name || $t('reviews.anonymous') }}</span>
                      <span v-if="getCommentSchool(comment)" class="comment-school">({{ getCommentSchool(comment) }})</span>
                      <span v-if="comment.ifSelf" class="badge yourself small">{{ $t('reviews.yourself') }}</span>
                    </div>
                    <span class="comment-date">{{ setup(comment.dateTime) }}</span>
                    <div class="translated-text-block">
                      <p class="comment-body">{{ getTranslatedCommentContent(comment) }}</p>
                      <button
                        v-if="canTranslateText(comment.content)"
                        class="translation-toggle comment-translation-toggle"
                        type="button"
                        :disabled="comment._translationLoading"
                        @click="toggleCommentTranslation(comment)"
                      >
                        {{ getTranslationToggleLabel(comment) }}
                      </button>
                    </div>
                    <div class="comment-actions">
                      <button v-if="!isGuest" class="comment-like-btn" :class="{ active: comment._liked }" @click="toggleCommentLike(comment)" :title="comment._liked ? 'Unlike comment' : 'Like comment'">
                        👍 <span>{{ comment._likeCount || 0 }}</span>
                      </button>
                      <button v-if="comment.ifSelf == 1" class="delete-comment" @click="handleDeleteComment(comment)">{{ $t('reviews.delete') }}</button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="comment-form">
                <textarea v-model="review._commentText" :placeholder="$t('reviews.leaveComment')"></textarea>
                <button class="submit-comment" @click="preaddReview(review)">{{ $t('reviews.submit') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Leaflet's default marker icons break under webpack bundling (assets don't
// resolve). Point them at the CDN copies so the pin renders.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});
import Swal from 'sweetalert2';
import RateThisPlaceButton from './_shared/RateThisPlaceButton.vue';
import ReviewsCountLabel from './_shared/ReviewsCountLabel.vue';
import { translateReviewText } from '@/utils/reviewTranslation';
export default {
  components: { RateThisPlaceButton, ReviewsCountLabel },
  name: 'HouseReviewScore',
  data() {
    return {
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
      resizeTimer: null,
      isSaved: false,
      showAllRatings: false,
      showExpandedStats: false,
      statusList:[],
      showOverlay: false,
      currentImage: null,
      showReviewForm: false,
      newReviewContent: '',
    reviews: [ // 示例评价数据
      {
        userid: 'User1',
        content: '非常满意的房产，性价比很高！',
        dateTime: '2025-05-23 10:00:00'
      },
      {
        userid: 'User2',
        content: '房子有点旧，但位置很好。',
        dateTime: '2025-05-23 11:00:00'
      }
    ],
    currentIndex: 0,


      images: [


        'image1.jpg',


        'image2.jpg',


        'image3.jpg'


      ],
    calleft: 0,
      speed:1,
    count:10 ,
    searchResults1:{},
    searchResults2:[],
    maplocation:{lat:0.0,lng:0.0},
    leafletMap: null,
    leafletMarker: null,
    selectedRoom: 0, // 当前选中的房间名称
    ratings: {
      Safety: 4.5,
      RentValue: 4.5,
      Location: 4.4,
      Utilities: 4.4,
      Cleanliness: 3,
      Internet: 3,
      NoiseLevel: 4.4,
      RoomSize: 4.4,
      Maintenance: 3,
      Roommates: 3
    },
      allShowimages:[],
      searchQuery2:'',
      reviewcomment:[],
      // Swipe carousel indices
      currentReviewIndex: 0,
      currentImageIndex: 0,
      // Swipe tracking
      swipeStartX: 0,
      swipeStartY: 0,
      swipeThreshold: 50,
      isSwiping: false,
      amazingCount: 0,
      goodCount: 0,
      averageCount: 0,
      poorCount: 0,
      awfulCount: 0,
      starSentence:'',
      isStarred:false,
      // Room-level like (room_like table, independent from favorites/review likes)
      roomLiked: false,
      roomLikeCount: 0,
      houseId: 0,
      houseLetId: 0,
      res: {},
      allinfo: {},
      reviewInfos: [],
      allRoomReviews: [],
      reviewFilter: 'all',
      reviewSearchQuery: '',
      reviewSortBy: 'latest',
      similarHouses: [],
      adminEditOpen: false,
      adminEditHouse: {},
      adminEditRooms: [],
      adminSaving: false
    };
  },
  computed: {
    isGuest() {
      // Guest = no token OR expired JWT. Mirrors hasValidToken() in router/index.js.
      const token = localStorage.getItem('token');
      if (!token) return true;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp && payload.exp < Date.now() / 1000) return true;
        return false;
      } catch (e) {
        return true;
      }
    },
    isAdmin() {
      const token = localStorage.getItem('token');
      if (!token) return false;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return (payload.role || '').toLowerCase() === 'admin';
      } catch (e) {
        return false;
      }
    },
    hasMapLocation() {
      return !!(this.maplocation && this.maplocation.lat && this.maplocation.lng);
    },
    displayedReviews() {
      let reviews;
      if (this.reviewFilter === 'all') {
        // Fall back to current room's reviews while allRoomReviews is still loading
        reviews = this.allRoomReviews.length ? this.allRoomReviews : this.reviewInfos;
      } else {
        reviews = this.reviewInfos;
      }
      // Apply search filter
      const q = (this.reviewSearchQuery || '').trim().toLowerCase();
      if (q) {
        reviews = reviews.filter(r => {
          const fields = [
            r.content,
            r.name,
            r.school,
            r.biggestProblem,
            r.tags,
            r._roomName,
            r.roomType,
            r.bedType
          ];
          return fields.some(f => f && String(f).toLowerCase().includes(q));
        });
      }
      // Apply sorting
      const sorted = [...reviews];
      // Helper to parse dateTime arrays [year,month,day,hour,min,sec,ms] into Date objects
      const parseDateTime = (dt) => {
        if (Array.isArray(dt)) {
          const [year, month, day, hour, min, sec, ms] = dt;
          return new Date(year, (month || 1) - 1, day || 1, hour || 0, min || 0, sec || 0, ms || 0);
        }
        return new Date(dt || 0);
      };
      switch (this.reviewSortBy) {
        case 'latest':
          sorted.sort((a, b) => parseDateTime(b.dateTime) - parseDateTime(a.dateTime));
          break;
        case 'earliest':
          sorted.sort((a, b) => parseDateTime(a.dateTime) - parseDateTime(b.dateTime));
          break;
        case 'rating-high':
          sorted.sort((a, b) => (parseFloat(b.average) || 0) - (parseFloat(a.average) || 0));
          break;
        case 'rating-low':
          sorted.sort((a, b) => (parseFloat(a.average) || 0) - (parseFloat(b.average) || 0));
          break;
        case 'price-high':
          sorted.sort((a, b) => (parseFloat(b.monthlyRent) || 0) - (parseFloat(a.monthlyRent) || 0));
          break;
        case 'price-low':
          sorted.sort((a, b) => (parseFloat(a.monthlyRent) || 0) - (parseFloat(b.monthlyRent) || 0));
          break;
      }
      return sorted;
    },
    currentRoomLabel() {
      return this.searchResults1 && this.searchResults1.houseLetName
        ? this.searchResults1.houseLetName
        : '';
    },
    addressDetailLine() {
      if (!this.res) return '';
      const parts = [];
      const seen = new Set();
      const norm = (s) => (s || '').trim().toLowerCase();
      for (const p of [this.res.detailedArea, this.res.name]) {
        const key = norm(p);
        if (key && !seen.has(key)) {
          seen.add(key);
          parts.push(p.trim());
        }
      }
      return parts.join(' · ');
    },
    amazingPercentage() {
      return this.totalReviews ? (this.amazingCount / this.totalReviews) * 100 : 0;
    },
    goodPercentage() {
      return this.totalReviews ? (this.goodCount / this.totalReviews) * 100 : 0;
    },
    averagePercentage() {
      return this.totalReviews ? (this.averageCount / this.totalReviews) * 100 : 0;
    },
    poorPercentage() {
      return this.totalReviews ? (this.poorCount / this.totalReviews) * 100 : 0;
    },
    awfulPercentage() {
      return this.totalReviews ? (this.awfulCount / this.totalReviews) * 100 : 0;
    },
    totalReviews() {
      return this.reviewInfos.length;
    },
    ratingCategoryList() {
      const toNumber = (val) => {
        const num = parseFloat(val);
        return isNaN(num) ? 0 : num;
      };
      return [
        { key: 'safety', value: toNumber(this.allinfo.safety) },
        { key: 'costTransparency', value: toNumber(this.allinfo.costTransparency) },
        { key: 'location', value: toNumber(this.allinfo.location) },
        { key: 'listingAccuracy', value: toNumber(this.allinfo.listingAccuracy) },
        { key: 'cleanliness', value: toNumber(this.allinfo.cleanliness) },
        { key: 'internetReliability', value: toNumber(this.allinfo.internetReliability) },
        { key: 'noiseLevel', value: toNumber(this.allinfo.noiseLevel) },
        { key: 'roomSize', value: toNumber(this.allinfo.roomSize) },
        { key: 'maintenance', value: toNumber(this.allinfo.maintenance) },
        { key: 'livingComfort', value: toNumber(this.allinfo.livingComfort) }
      ];
    },
    topCategoryKey() {
      const list = this.ratingCategoryList;
      if (!list.length) return null;
      return list.reduce((top, item) => (item.value > top.value ? item : top), list[0]).key;
    },
    lowestCategoryKey() {
      const list = this.ratingCategoryList;
      if (!list.length) return null;
      const filtered = list.filter(item => item.key !== this.topCategoryKey);
      const targetList = filtered.length ? filtered : list;
      return targetList.reduce((low, item) => (item.value < low.value ? item : low), targetList[0]).key;
    },
    topCategoryLabel() {
      const labels = {
        safety: 'Safety',
        costTransparency: 'Cost Transparency',
        location: 'Location Convenience',
        listingAccuracy: 'Listing Accuracy',
        cleanliness: 'Cleanliness & Condition',
        internetReliability: 'Internet & Utilities Reliability',
        noiseLevel: 'Noise Exposure',
        roomSize: 'Space Adequacy',
        maintenance: 'Maintenance Responsiveness',
        livingComfort: 'Living Comfort'
      };
      return labels[this.topCategoryKey] || 'Top Rated';
    },
    lowestCategoryLabel() {
      const labels = {
        safety: 'Safety',
        costTransparency: 'Cost Transparency',
        location: 'Location Convenience',
        listingAccuracy: 'Listing Accuracy',
        cleanliness: 'Cleanliness & Condition',
        internetReliability: 'Internet & Utilities Reliability',
        noiseLevel: 'Noise Exposure',
        roomSize: 'Space Adequacy',
        maintenance: 'Maintenance Responsiveness',
        livingComfort: 'Living Comfort'
      };
      return labels[this.lowestCategoryKey] || 'Lowest Rated';
    },
    currentStudentReview() {
      if (!this.reviewInfos.length) return null;
      const sortedReviews = [...this.reviewInfos].sort((a, b) => 
        parseFloat(b.average || 0) - parseFloat(a.average || 0)
      );
      return sortedReviews[this.currentReviewIndex] || sortedReviews[0] || null;
    },
    totalStudentReviews() {
      return Math.min(this.reviewInfos.length, 5);
    },
    currentPhoto() {
      if (!this.allShowimages.length) return null;
      return this.allShowimages[this.currentImageIndex] || this.allShowimages[0];
    },
    isMobile() {
      return this.windowWidth <= 480;
    },
    isTablet() {
      return this.windowWidth > 480 && this.windowWidth <= 1024;
    },
    isDesktop() {
      return this.windowWidth > 1024;
    },
    layoutClass() {
      if (this.isMobile) return 'mobile-layout';
      if (this.isTablet) return 'tablet-layout';
      return 'desktop-layout';
    },
    averageStayDuration() {
      const reviews = this.allRoomReviews && this.allRoomReviews.length ? this.allRoomReviews : this.reviewInfos;
      if (!reviews || !reviews.length) return 'N/A';
      const durations = reviews.map(r => parseInt(r.stayDuration) || 0).filter(d => d > 0);
      if (!durations.length) return 'N/A';
      const avg = durations.reduce((sum, d) => sum + d, 0) / durations.length;
      const rounded = Math.round(avg);
      if (rounded === 1) return '< 3 months';
      if (rounded === 2) return '3–6 months';
      if (rounded === 3) return '6–12 months';
      if (rounded >= 4) return '1+ year';
      return 'N/A';
    },
    averageMonthlyRentText() {
      const reviews = this.allRoomReviews && this.allRoomReviews.length ? this.allRoomReviews : this.reviewInfos;
      if (!reviews || !reviews.length) return 'Average rent not available';
      const rents = reviews
        .map(r => Number(r.monthlyRent))
        .filter(rent => Number.isFinite(rent) && rent > 0);
      if (!rents.length) return 'Average rent not available';
      const avgRent = Math.round(rents.reduce((sum, rent) => sum + rent, 0) / rents.length);
      return `Average rent: HK$${avgRent.toLocaleString()}/mo`;
    },
    mobilePriceInfo() {
      const roomPriceCandidates = [
        this.searchResults1 && this.searchResults1.monthlyRent,
        this.res && this.res.monthlyRent,
      ]
        .map(value => Number(value))
        .filter(value => Number.isFinite(value) && value > 0);

      if (roomPriceCandidates.length) {
        return {
          value: Math.round(roomPriceCandidates[0]).toLocaleString(),
          unit: 'HKD',
        };
      }

      const reviews = this.allRoomReviews && this.allRoomReviews.length ? this.allRoomReviews : this.reviewInfos;
      const rents = (reviews || [])
        .map(review => Number(review.monthlyRent))
        .filter(value => Number.isFinite(value) && value > 0);

      if (!rents.length) return null;

      const avgRent = Math.round(rents.reduce((sum, rent) => sum + rent, 0) / rents.length);
      return {
        value: avgRent.toLocaleString(),
        unit: 'avg',
      };
    },
    mostCommonRoomType() {
      const reviews = this.allRoomReviews && this.allRoomReviews.length ? this.allRoomReviews : this.reviewInfos;
      if (!reviews || !reviews.length) return null;
      const counts = {};
      reviews.forEach(r => {
        const rt = r.roomType;
        if (rt) counts[rt] = (counts[rt] || 0) + 1;
      });
      const entries = Object.entries(counts);
      if (!entries.length) return null;
      return entries.reduce((a, b) => b[1] > a[1] ? b : a)[0];
    },
    topTagsList() {
      const reviews = this.allRoomReviews && this.allRoomReviews.length ? this.allRoomReviews : this.reviewInfos;
      if (!reviews || !reviews.length) return [];
      const tagCounts = {};
      reviews.forEach(r => {
        const tagsStr = r.tags;
        if (tagsStr && typeof tagsStr === 'string') {
          tagsStr.split(',').map(t => t.trim()).filter(Boolean).forEach(tag => {
            tagCounts[tag] = (tagCounts[tag] || 0) + 1;
          });
        }
      });
      return Object.entries(tagCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);
    },
    mostCommonBedType() {
      const reviews = this.allRoomReviews && this.allRoomReviews.length ? this.allRoomReviews : this.reviewInfos;
      if (!reviews || !reviews.length) return null;
      const counts = {};
      reviews.forEach(r => {
        const bt = r.bedType;
        if (bt) counts[bt] = (counts[bt] || 0) + 1;
      });
      const entries = Object.entries(counts);
      if (!entries.length) return null;
      return entries.reduce((a, b) => b[1] > a[1] ? b : a)[0];
    },
    sharedBathroomSummary() {
      const reviews = this.allRoomReviews && this.allRoomReviews.length ? this.allRoomReviews : this.reviewInfos;
      if (!reviews || !reviews.length) return 'N/A';
      const withData = reviews.filter(r => r.isShared !== undefined && r.isShared !== null);
      if (!withData.length) return 'N/A';
      const yesCount = withData.filter(r => r.isShared == 1).length;
      return yesCount > withData.length / 2 ? 'Yes' : 'No';
    }
  },
  // (watch block merged below — keep a single watch object so Vue does not
  // silently drop one of them; the duplicate previously broke route changes
  // triggered by the "Similar Houses Nearby" navigation.)
  mounted() {
    this.houseId = this.$route.query.houseId;
    this.houseLetId = this.$route.query.houseLetId;

    if (this.houseId && this.houseLetId) {
      this.pregetSentence();
    }

    this.windowWidth = window.innerWidth;
    window.addEventListener('resize', this.handleResize);

    this.initLeafletMap();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    if (this.timer) {
      clearInterval(this.timer);
    }
    if (this.sliderTimer) {
      clearInterval(this.sliderTimer);
    }
    if (this.leafletMap) {
      this.leafletMap.remove();
      this.leafletMap = null;
    }
  },
  watch: {
    '$route.query.houseId'(newId) {
      if (!newId) return;
      this.houseId = newId;
      this.houseLetId = this.$route.query.houseLetId || 0;
      this.selectedRoom = 0;
      this.reviewFilter = 'all';
      this.similarHouses = [];
      this.reviewInfos = [];
      this.allRoomReviews = [];
      this.res = {};
      this.allinfo = {};
      this.roomLiked = false;
      this.roomLikeCount = 0;
      this.fetchHouseInfo(newId);
      this.getHouseLetInfo(newId);
      this.getallinfo(newId, this.houseLetId, this);
      this.reviewInfo(newId, this.houseLetId, this);
      this.fetchRoomLikeStatus();
      if (newId && this.houseLetId) {
        this.pregetSentence();
      }
    },
    'maplocation.lat'() { this.updateLeafletMarker(); },
    'maplocation.lng'() { this.updateLeafletMarker(); },
  },
  created() {
    const houseId = this.$route.query.houseId;
    this.houseId = this.$route.query.houseId;
    this.houseLetId = this.$route.query.houseLetId;
    // Default to "All Rooms" view so users see all reviews
    this.selectedRoom = 0;
    this.reviewFilter = 'all';
    console.log(houseId)
    console.log("houseID")
    this.fetchHouseInfo(houseId);
    this.getHouseLetInfo(this.houseId);
    this.getallinfo(this.houseId,this.houseLetId,this);
    this.reviewInfo(this.houseId,this.houseLetId,this);
    this.fetchRoomLikeStatus();
    // this.move(); // Disabled: calleft is not used in the template and the 50fps setInterval causes unnecessary reactive updates
  },
  props: ["dataList"],
  methods: {
    canTranslateText(text) {
      return !!(this.$i18n && this.$i18n.locale !== 'en' && String(text || '').trim());
    },
    getTranslationToggleLabel(item) {
      if (item && item._translationLoading) return this.$t('translation.translating');
      if (item && item._translatedContent && !item._showOriginal) return this.$t('translation.seeOriginal');
      return this.$t('translation.seeTranslation');
    },
    getTranslatedReviewContent(review) {
      if (!review) return this.$t('reviews.noContent');
      if (review._translatedContent && !review._showOriginal) return review._translatedContent;
      return review.content || this.$t('reviews.noContent');
    },
    getTranslatedCommentContent(comment) {
      if (!comment) return '';
      if (comment._translatedContent && !comment._showOriginal) return comment._translatedContent;
      return comment.content || '';
    },
    async toggleReviewTranslation(review) {
      await this.toggleTextTranslation(review, review && review.content);
    },
    async toggleCommentTranslation(comment) {
      await this.toggleTextTranslation(comment, comment && comment.content);
    },
    async toggleTextTranslation(item, sourceText) {
      if (!item || item._translationLoading || !this.canTranslateText(sourceText)) return;

      if (item._translatedContent) {
        this.$set(item, '_showOriginal', !item._showOriginal);
        return;
      }

      this.$set(item, '_translationLoading', true);
      try {
        const translated = await translateReviewText(sourceText, this.$i18n.locale);
        this.$set(item, '_translatedContent', translated);
        this.$set(item, '_showOriginal', false);
      } catch (e) {
        this.$set(item, '_translatedContent', sourceText);
        this.$set(item, '_showOriginal', true);
      } finally {
        this.$set(item, '_translationLoading', false);
      }
    },
    setReviewSort(val) {
      console.log('setReviewSort called with:', val);
      this.reviewSortBy = val;
    },
    tagColorClass(index) {
      const colors = ['purple', 'cyan', 'blue', 'green', 'pink', 'orange', 'purple', 'cyan'];
      return colors[index % colors.length];
    },
    handleResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        this.windowWidth = window.innerWidth;
      }, 150);
    },
    getCategoryLabel(key) {
      const labels = {
        safety: 'Safety',
        costTransparency: 'Cost Transparency',
        location: 'Location Convenience',
        listingAccuracy: 'Listing Accuracy',
        cleanliness: 'Cleanliness & Condition',
        internetReliability: 'Internet & Utilities Reliability',
        noiseLevel: 'Noise Exposure',
        roomSize: 'Space Adequacy',
        maintenance: 'Maintenance Responsiveness',
        livingComfort: 'Living Comfort'
      };
      return labels[key] || key;
    },
    getStayDurationLabel(val) {
      const map = { 1: '< 3 months', 2: '3–6 months', 3: '6–12 months', 4: '1+ year' };
      return map[val] || (val ? val + ' yr' + (val != 1 ? 's' : '') : '');
    },
    getValidTags(emailField) {
      if (!emailField || typeof emailField !== 'string') return [];
      const known = ['Nearby university','Far from university','Clean','Dirty','Quiet','Noisy','Safe','Unsafe','Spacious','Cramped','Worth the price','Not worth the price','Accurate listing','Misleading photos','Transparent pricing','Hidden fees','Well maintained','Poor maintenance','Convenient location','Inconvenient location'];
      return emailField.split(',').map(t => t.trim()).filter(t => t && known.includes(t));
    },
    ToAddHouseRoom() {
      const id = this.houseId
      this.$router.push({
        path: '/AddHouseRoom',
        query: { houseId:id }
      });
    },
    editDetails(id) {
      this.$router.push({
        path: "/editReview",
        query: { id }
      });
    },

    // 查看详情
    viewDetails(id) {
      if (!id || id === 'undefined' || id === 'null' || id === 0) {
        console.error("Invalid review ID provided:", id);
        Swal.fire({
          icon: 'error',
          title: 'Invalid Review',
          text: 'The review ID is invalid. Please try again.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#3CC2FF'
        });
        return;
      }
      console.log("Viewing details for review ID:", id);
      const review = this.reviewInfos.find(r => r.id == id);
      if (review) {
        console.log("Review found in list - ID:", review.id, "Average:", review.average);
        this.$router.push({
          path: "/showReview",
          query: { 
            id: String(id),
            expectedAverage: review.average
          }
        });
      } else {
        console.warn("Review not found in reviewInfos array, ID:", id);
        this.$router.push({
          path: "/showReview",
          query: { id: String(id) }
        });
      }
    },

    confirmDeleteComment(commentId) {
      Swal.fire({
        title: "Are you sure?",
        text: "This will permanently delete the comment.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, cancel",
        confirmButtonColor: '#3CC2FF'
      }).then((result) => {
        if (result.isConfirmed) {
          axios
            .get(`/api/review/deleteComment?commentId=${commentId}`)
            .then((res) => {
              console.log(res);
              Swal.fire({
                icon: 'success',
                title: 'Comment deleted',
                text: 'Your comment has been removed',
                showConfirmButton: true,
                confirmButtonColor: '#3CC2FF'
              }).then(() => {
                if (this.houseId && this.houseLetId) {
                  this.reviewInfo(this.houseId, this.houseLetId, this);
                }
              });
            })
            .catch((err) => {
              console.error(err);
              Swal.fire({
                icon: "error",
                title: "Error!",
                text: "Failed to delete comment. Please try again.",
                confirmButtonColor: '#3CC2FF'
              });
            });
        }
      });
    },
    confirmDelete(id) {
      Swal.fire({
        title: "Are you sure?",
        text: "This will permanently delete the review.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, cancel",
        confirmButtonColor: '#3CC2FF'
      }).then((result) => {
        if (result.isConfirmed) {
          axios
            .get(`/api/review/deleteReview?reviewId=${id}`)
            .then((res) => {
              console.log(res)
              Swal.fire({
                icon: 'success',
                title: 'Your review has been removed',
                showConfirmButton: true,
                confirmButtonColor: '#3CC2FF'
              }).then(() => {
                this.reviewInfo(this.houseId, this.houseLetId, this);
                this.getallinfo(this.houseId, this.houseLetId, this);
              });
            })
            .catch((err) => {
              console.error(err);
              Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: err?.response?.data?.msg || 'Failed to delete review.',
                confirmButtonColor: '#3CC2FF'
              });
            });
        }
      });
    },
    confirmDeleteAddress() {
      const name = (this.res && this.res.name) ? this.res.name : 'this address';
      Swal.fire({
        title: 'Delete Address?',
        html: `This will permanently delete <strong>${name}</strong> and ALL its rooms, reviews, ratings, and data. This cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#dc2626'
      }).then((result) => {
        if (!result.isConfirmed) return;
        axios.delete(`/api/house/admin/delete/${this.houseId}`)
          .then(() => {
            Swal.fire({
              icon: 'success',
              title: 'Address deleted',
              text: `${name} has been permanently removed.`,
              confirmButtonColor: '#3CC2FF'
            }).then(() => {
              this.$router.push('/');
            });
          })
          .catch((err) => {
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: err?.response?.data?.msg || 'Failed to delete address. Please try again.',
              confirmButtonColor: '#3CC2FF'
            });
          });
      });
    },
    openAdminEdit() {
      this.adminEditHouse = Object.assign({}, this.res);
      this.adminEditRooms = (this.searchResults2 || []).map(r => Object.assign({}, r));
      this.adminEditOpen = !this.adminEditOpen;
    },
    saveAdminHouseEdit() {
      this.adminSaving = true;
      axios.put(`/api/house/admin/update/${this.houseId}`, this.adminEditHouse)
        .then(() => {
          Swal.fire({ icon: 'success', title: 'Address updated', timer: 1500, showConfirmButton: false });
          this.getHouseInfo(this, this.houseId);
        })
        .catch(err => {
          Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.msg || 'Failed to save.', confirmButtonColor: '#3CC2FF' });
        })
        .finally(() => { this.adminSaving = false; });
    },
    saveAdminRoomEdit(room) {
      this.adminSaving = true;
      axios.put(`/api/house/admin/room/update/${room.id}`, room)
        .then(() => {
          Swal.fire({ icon: 'success', title: 'Room updated', timer: 1500, showConfirmButton: false });
          this.getHouseLetInfo(this.houseId);
        })
        .catch(err => {
          Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.msg || 'Failed to save.', confirmButtonColor: '#3CC2FF' });
        })
        .finally(() => { this.adminSaving = false; });
    },
    confirmDeleteRoom(room) {
      const label = room.houseLetName || room.houseLet || `Room ${room.id}`;
      Swal.fire({
        title: 'Delete Room?',
        html: `This will permanently delete <strong>${label}</strong> and all its reviews and ratings.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#dc2626'
      }).then(result => {
        if (!result.isConfirmed) return;
        axios.delete(`/api/house/admin/room/delete/${room.id}`)
          .then(() => {
            Swal.fire({ icon: 'success', title: 'Room deleted', timer: 1500, showConfirmButton: false });
            this.adminEditRooms = this.adminEditRooms.filter(r => r.id !== room.id);
            this.getHouseLetInfo(this.houseId);
          })
          .catch(err => {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.msg || 'Failed to delete room.', confirmButtonColor: '#3CC2FF' });
          });
      });
    },
    canDeleteReview(review) {
      const token = localStorage.getItem('token');
      if (!token) return false;
      let isAdmin = false;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp && payload.exp < Date.now() / 1000) return false;
        isAdmin = (payload.role || '').toLowerCase() === 'admin';
      } catch (e) { return false; }
      return review.ifSelf == 1 || isAdmin;
    },
    toBaiduHTTP(){
      // Opens Google Maps in a new tab with the pinned location. Name kept for
      // backward compatibility with the existing button handler.
      if (!this.hasMapLocation) return;
      const url = `https://www.google.com/maps?q=${this.maplocation.lat},${this.maplocation.lng}`;
      window.open(url, '_blank');
    },
    initLeafletMap() {
      if (!this.$refs.mapEl) return;
      const lat = this.hasMapLocation ? this.maplocation.lat : 22.3193;
      const lng = this.hasMapLocation ? this.maplocation.lng : 114.1694;
      this.leafletMap = L.map(this.$refs.mapEl, { zoomControl: true }).setView([lat, lng], this.hasMapLocation ? 16 : 11);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap'
      }).addTo(this.leafletMap);
      if (this.hasMapLocation) {
        this.leafletMarker = L.marker([lat, lng]).addTo(this.leafletMap);
      }
    },
    updateLeafletMarker() {
      if (!this.leafletMap || !this.hasMapLocation) return;
      const lat = this.maplocation.lat;
      const lng = this.maplocation.lng;
      if (this.leafletMarker) {
        this.leafletMarker.setLatLng([lat, lng]);
      } else {
        this.leafletMarker = L.marker([lat, lng]).addTo(this.leafletMap);
      }
      this.leafletMap.setView([lat, lng], 16);
    },
          allImages() {
    const allImages = [];

    this.reviewInfos.forEach(item => {
      if (item.file && typeof item.file === 'string') {
        const images = this.getimages(item.file);
        allImages.push(...images);
      }
    });

    console.log("allimages")
    console.log(allImages)
    this.allShowimages = allImages;
    return allImages;
  },
    formatSafety(value) {
      const num = parseFloat(value);
      if (Number.isNaN(num)) {
        return '0.0';
      }
      const clamped = Math.max(0, Math.min(5, num));
      return clamped.toFixed(1);
    },
    getRatingColorClass(value) {
      const score = parseFloat(value || 0);
      if (score >= 3.8) return 'rating-green';
      if (score >= 3.0) return 'rating-yellow';
      return 'rating-red';
    },
    // formatDate(dateString) {
    //   return dayjs(dateString).format('MMM DD, YYYY');
    // },
    formatDate(dateInput) {
  if (!dateInput) return '';
  // New format (Jackson ISO string) — handles "2025-08-28T03:26:51" and "2025-08-28 03:26:51"
  if (typeof dateInput === 'string') {
    return dayjs(dateInput).format('MMM DD, YYYY');
  }
  // Legacy format (Jackson LocalDateTime-as-array [y, M, d, h, m, s, ms])
  if (Array.isArray(dateInput)) {
    const [year, month = 1, day = 1, hour = 0, minute = 0, second = 0, millisecond = 0] = dateInput;
    return dayjs(new Date(year, month - 1, day, hour, minute, second, millisecond)).format('MMM DD, YYYY');
  }
  return '';
},

    getScoreClass(value) {
      const score = parseFloat(value || 0);
      if (score >= 3.8) return 'score-excellent';
      if (score >= 3.0) return 'score-good';
      return 'score-poor';
    },
    // getimages(imageUrls) {
    //   if (!imageUrls) return [];
    //   return imageUrls.split(',').filter(img => img.trim());
    // },
    openImageModal(image) {
      this.currentImage = image;
      this.showOverlay = true;
    },
    closeOverlay() {
      this.showOverlay = false;
      this.currentImage = null;
    },
    handleImageError(event) {
      // Don't replace with avatar - just hide the broken image
      event.target.style.display = 'none';
    },
    copyReviewInfo() {
      const house = this.res || {};
      const info = this.allinfo || {};
      const reviews = this.reviewInfos || [];
      const rating = info.average != null ? parseFloat(info.average).toFixed(1) : 'N/A';
      const ratingLine = reviews.length > 0
        ? `⭐ Overall Rating: ${rating}/5.0 (${reviews.length} review${reviews.length !== 1 ? 's' : ''})`
        : `⭐ ${this.$t('emptyState.notReviewedYet')}`;

      // Build house info section
      const lines = [
        `🏠 ${house.name || 'Unknown Property'}`,
        `📍 ${[house.street, house.area, house.district, house.region].filter(Boolean).join(', ') || 'Location not available'}`,
        ratingLine,
        `🏷️ Provider: ${house.type || 'N/A'}`,
      ];

      // Add category breakdown only when there is at least one review to base it on,
      // otherwise every category would show a fabricated 0.0/5 default.
      if (reviews.length > 0) {
        lines.push('', '📊 Category Ratings:');
        const categories = [
          { key: 'safety', label: 'Safety' },
          { key: 'location', label: 'Location Convenience' },
          { key: 'cleanliness', label: 'Cleanliness & Condition' },
          { key: 'noiseLevel', label: 'Noise Exposure' },
          { key: 'maintenance', label: 'Maintenance Responsiveness' },
          { key: 'listingAccuracy', label: 'Listing Accuracy' },
          { key: 'costTransparency', label: 'Cost Transparency' },
          { key: 'roomSize', label: 'Space Adequacy' },
          { key: 'internetReliability', label: 'Internet & Utilities Reliability' },
          { key: 'livingComfort', label: 'Living Comfort' },
        ];
        categories.forEach(cat => {
          const val = info[cat.key];
          if (val != null) {
            lines.push(`   ${cat.label}: ${parseFloat(val).toFixed(1)}/5`);
          }
        });
      }

      // Add up to 3 recent reviews
      if (reviews.length > 0) {
        lines.push('', '💬 Recent Reviews:');
        reviews.slice(0, 3).forEach((r, i) => {
          const reviewer = r.name || 'Anonymous';
          const text = (r.content || '').slice(0, 120);
          const reviewRating = r.average != null ? parseFloat(r.average).toFixed(1) : '?';
          lines.push(`${i + 1}. ${reviewer} (${reviewRating}★): "${text}${r.content && r.content.length > 120 ? '…' : ''}"`);
        });
        if (reviews.length > 3) {
          lines.push(`   ...and ${reviews.length - 3} more review${reviews.length - 3 !== 1 ? 's' : ''}`);
        }
      }

      const copyText = lines.join('\n');

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(copyText).then(() => {
          Swal.fire({ icon: 'success', title: 'Copied!', text: 'Review & house info copied to clipboard', timer: 1500, showConfirmButton: false });
        }).catch(() => this._fallbackCopy(copyText));
      } else {
        this._fallbackCopy(copyText);
      }
    },
    _fallbackCopy(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      Swal.fire({ icon: 'success', title: 'Copied!', text: 'Review & house info copied to clipboard', timer: 1500, showConfirmButton: false });
    },
    handleSelectChange2(value) {
      // value=0 means "All Rooms"; otherwise it is the houseLetId of the selected room.
      // Spec 3.4: switching the dropdown swaps rating, photos, comments, likes, reviews.
      this.selectedRoom = value;
      this.houseLetId = value || 0;
      this.reviewInfos = [];
      this.allRoomReviews = [];
      this.roomLiked = false;
      this.roomLikeCount = 0;
      this.getallinfo(this.houseId, this.houseLetId, this);
      this.reviewInfo(this.houseId, this.houseLetId, this);
      if (this.houseLetId) {
        this.fetchRoomLikeStatus();
      }
    },
    ToRegistInfo() {
      this.$router.push({ 
        path: '/rateTheHouse', 
        query: { 
          houseId: this.houseId, 
          houseLetId: this.houseLetId 
        } 
      });
    },
    // prehandlelike(index) {
    //   const review = this.reviewInfos[index];
    //   review.liked += 1;
    //   // Here you would typically make an API call
    // },
    // prehandleunlike(index) {
    //   const review = this.reviewInfos[index];
    //   review.unliked += 1;
    //   // Here you would typically make an API call
    // },

    fetchReviewInfo() {
      // Simulate API call - replace with actual API
      console.log('Fetching review info for:', this.houseId, this.houseLetId);
    },








    // Tian Method
    
  openOverlay(image) {
      if (this.isGuest) { this.goLogin(); return; }
      this.currentImage = image;
      this.showOverlay = true;
    },
    goLogin() {
      try {
        localStorage.setItem('redirectAfterLogin', this.$route.fullPath);
      } catch (e) { /* storage full / disabled — fine */ }
      this.$router.push({ path: '/LogReg', query: { redirect: this.$route.fullPath } });
    },

  pregetSentence(){
    this.getSentence(this)
},
  getSentence(obj){
      axios.get('/api/house/searchStarHouse', {
        params: {
          houseId: this.houseLetId,
        },
        timeout: 10000
      })
      .then(function (response) {
        console.log("searchStarHouse")
        console.log(response.data.data)
        obj.isStarred = response.data.data
        obj.isSaved = !!response.data.data
      })
      .catch(function (error) {
        console.log(error);
      });
  },
    // DEPRECATED: toggleSave / toggleStar write to the favorites table (house-level).
    // The favorites table is deprecated. New code uses toggleRoomLike (room_like table).
    // These methods are kept for backward compatibility only — no new UI should call them.
    toggleSave() {
      // No-op: favorites/save button removed. Room-level like is now the primary action.
    },
    toggleStar() {
      // No-op: favorites/save button removed. Room-level like is now the primary action.
    },

    // ---- Room-level like (room_like table) ----
    fetchRoomLikeStatus() {
      if (this.isGuest || !this.houseLetId) return;
      axios.get('/api/room-like/status', {
        params: { houseLetId: this.houseLetId },
        timeout: 10000
      }).then(response => {
        const data = response.data && response.data.data;
        if (data) {
          this.roomLiked = !!data.liked;
          this.roomLikeCount = data.likeCount || 0;
        }
      }).catch(err => {
        console.warn('Failed to fetch room like status:', err);
      });
    },
    toggleRoomLike() {
      if (this.isGuest) { this.goLogin(); return; }
      if (!this.houseLetId) return;
      // Optimistic UI update
      this.roomLiked = !this.roomLiked;
      this.roomLikeCount += this.roomLiked ? 1 : -1;
      axios.post('/api/room-like/toggle', { houseLetId: this.houseLetId }, { timeout: 10000 })
        .then(response => {
          const data = response.data && response.data.data;
          if (data) {
            this.roomLiked = !!data.liked;
            this.roomLikeCount = data.likeCount || 0;
          }
        })
        .catch(err => {
          // Revert optimistic update on error
          this.roomLiked = !this.roomLiked;
          this.roomLikeCount += this.roomLiked ? 1 : -1;
          console.error('Room like toggle failed:', err);
        });
    },

    // ---- Comment-level like (comment_like table) ----
    toggleCommentLike(comment) {
      if (this.isGuest) { this.goLogin(); return; }
      if (!comment || !comment.id) return;
      // Optimistic UI update
      const wasLiked = !!comment._liked;
      comment._liked = !wasLiked;
      comment._likeCount = (comment._likeCount || 0) + (comment._liked ? 1 : -1);
      this.$forceUpdate();
      axios.post('/api/comment-like/toggle', { commentId: comment.id }, { timeout: 10000 })
        .then(response => {
          const data = response.data && response.data.data;
          if (data) {
            comment._liked = !!data.liked;
            comment._likeCount = data.likeCount || 0;
            this.$forceUpdate();
          }
        })
        .catch(err => {
          // Revert optimistic update on error
          comment._liked = wasLiked;
          comment._likeCount = (comment._likeCount || 0) + (wasLiked ? 1 : -1);
          this.$forceUpdate();
          console.error('Comment like toggle failed:', err);
        });
    },
  preaddReview(review){
    this.addReview(this, review);
  },
  addReview(obj, review) {
      const commentText = (review._commentText || '').trim();
      if (commentText === '') {
        Swal.fire({
          icon: 'warning',
          title: 'Empty Comment',
          text: 'Comment content cannot be empty!',
          confirmButtonColor: '#3CC2FF'
        });
        return;
      }
      axios.get('/api/house/addComment', {
        params: {
          rateId: review.id,
          content: commentText,
        },
        timeout: 10000
      })
      .then((response) => {
        console.log(response);
        const body = response?.data || {};
        if (body.code !== undefined && body.code !== 1) {
          Swal.fire({
            icon: 'error',
            title: 'Could not post',
            text: body.msg || 'Failed to add comment.',
            confirmButtonColor: '#3CC2FF'
          });
          return;
        }
        review._commentText = '';
        // Targeted refresh: just refetch this review's comments and splice into place
        // so the new comment appears immediately without a full page reload.
        axios.get('/api/house/searchComment', { params: { rateId: review.id }, timeout: 8000 })
          .then((rc) => {
            const fresh = (rc && rc.data && (rc.data.data || rc.data)) || [];
            obj.$set(review, 'comments', Array.isArray(fresh) ? fresh : (review.comments || []));
            obj.$forceUpdate();
          })
          .catch(() => obj.$forceUpdate());
        obj.reviewInfo(obj.houseId, obj.houseLetId, obj);
        obj.getallinfo(obj.houseId, obj.houseLetId, obj);
        Swal.fire({
          icon: 'success',
          title: 'Comment added',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2200,
          confirmButtonColor: '#3CC2FF'
        });
      })
      .catch((error) => {
        console.log(error);
        const msg = error?.response?.data?.msg || error?.response?.data?.message || 'Something went wrong.';
        Swal.fire({ icon: 'error', title: 'Error', text: msg, confirmButtonColor: '#3CC2FF' });
      });
    },
  // presearchComment(index){
  //   const resp = this.searchComment(this,index)
  //   console.log("resp:")
  //   console.log(resp)
  //   return resp
  // },
  // searchComment(obj,index){
  //
  //         axios.get('/house/searchComment', {
  //                     params: {
  //                           rateId: this.reviewInfos[index].id
  //                     }
  //                 })
  //                 .then(function (response) {
  //               console.log(response.data.data);
  //               return response.data.data
  //
  //                 })
  //                 .catch(function (error) {
  //                   console.log(error)
  //                 });
  //
  //
  // },
  // getimages(imageUrls){
  //         // 将字符串按逗号分割为数组
  //         const imagesArray = imageUrls.split(',');
  //         // 输出结果

  //         console.log(imagesArray);
  //         return imagesArray
  // },

   getimages(imageUrls) {
      if (!imageUrls) return [];
      const imagesArray = String(imageUrls).split(',');
      const relativePaths = imagesArray.map((raw) => {
        const url = String(raw || '').trim();
        if (!url) return '';
        const match = url.match(/^https?:\/\/(?:127\.0\.0\.1|localhost):9005(\/reviewimages\/[^?]+)(\?.*)?$/);
        if (match) return match[1] + (match[2] || '');
        if (url.startsWith('/reviewimages/')) return url;
        return url;
      });
      return relativePaths.filter(Boolean);
    },
  startSlider() {


      this.sliderTimer = setInterval(() => {


        this.currentIndex = (this.currentIndex + 1) % this.images.length;


      }, 3000);


    },
  //移动
    move() {
      this.timer = setInterval(this.starmove, 20);
    },
    //开始移动
    starmove() {
      this.calleft -= 1.2;//*this.speed
      if (this.calleft <= -1150) {
        this.calleft = 0;
      }
    },
    //鼠标悬停时停止移动
    stopmove() {
      clearInterval(this.timer);
    },
    //点击按钮左移
    zuohua() {
      this.calleft -= 240;//this.speed = 1
      if (this.calleft <= -1200) {
        this.calleft = 0;
      }
    },
    //点击按钮右移
    youhua() { //this.speed = -1
      this.calleft += 240;
      if (this.calleft >= 0) {
        this.calleft = -1200;
      }
    },
  prehandlelike(review){
    this.handleLike(this, review);
  },
  handleLike(obj, review) {
      const id = review.id;
      console.log(id);
      axios.put(`/api/house/like/${id}`, null, { timeout: 10000 })
        .then(function (response) {
          console.log(response)
          if(review.ifliked==0){
            review.ifliked=1;
            review.liked=review.liked+1;
          }else{
            review.ifliked=0;
            review.liked=review.liked-1;
          }
          obj.$forceUpdate();
        })
        .catch(function (error) {
          console.log(error)
        });
  },



   prehandleunlike(review){
    this.handleunLike(this, review);
  },
  handleunLike(obj, review) {
      const id = review.id;
      console.log(id);
      axios.put(`/api/house/unlike/${id}`, null, { timeout: 10000 })
        .then(function (response) {
          console.log(response)
          if(review.ifunliked==0){
            review.ifunliked=1;
            review.unliked=review.unliked+1;
          }else{
            review.ifunliked=0;
            review.unliked=review.unliked-1;
          }
          obj.$forceUpdate();
        })
        .catch(function (error) {
          console.log(error)
        });
  },
  // setup(backendDateTime) {
  //     return dayjs(backendDateTime.value).format("YYYY-MM-DD HH:mm:ss");
  // },
  setup(dateTimeInput) {
      if (Array.isArray(dateTimeInput)) {
        const [year, month, day, hour, minute, second] = dateTimeInput;
        const date = new Date(year, month - 1, day, hour, minute, second);
        return dayjs(date).format("YYYY-MM-DD HH:mm:ss");
      } else if (dateTimeInput) {
        return dayjs(dateTimeInput).format("YYYY-MM-DD HH:mm:ss");
      } else {
        return "Invalid date format";
      }
    },
    toggleComment(review) {
      console.log("toggleComment")
      this.$set(review, '_showComments', !review._showComments);
      if (!review._commentText) {
        this.$set(review, '_commentText', '');
      }
    },
    toggleAllRatings() {
      this.showAllRatings = !this.showAllRatings;
    },
    // Swipe functionality for reviews carousel
    handleReviewSwipeStart(e) {
      if (e.touches) {
        this.swipeStartX = e.touches[0].clientX;
        this.swipeStartY = e.touches[0].clientY;
      } else {
        this.swipeStartX = e.clientX;
        this.swipeStartY = e.clientY;
      }
    },
    handleReviewSwipeEnd(e) {
      if (!this.swipeStartX) return;
      
      let endX, endY;
      if (e.changedTouches) {
        endX = e.changedTouches[0].clientX;
        endY = e.changedTouches[0].clientY;
      } else {
        endX = e.clientX;
        endY = e.clientY;
      }
      
      const diffX = this.swipeStartX - endX;
      const diffY = this.swipeStartY - endY;
      
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > this.swipeThreshold) {
        this.isSwiping = true;
        const sortedReviews = [...this.reviewInfos].sort((a, b) => 
          parseFloat(b.average || 0) - parseFloat(a.average || 0)
        );
        const maxIndex = Math.min(sortedReviews.length, 5) - 1;
        
        if (diffX > 0) {
          this.currentReviewIndex = Math.min(this.currentReviewIndex + 1, maxIndex);
        } else {
          this.currentReviewIndex = Math.max(this.currentReviewIndex - 1, 0);
        }
        
        if (e.preventDefault) e.preventDefault();
        setTimeout(() => { this.isSwiping = false; }, 300);
      } else {
        this.isSwiping = false;
      }
      
      this.swipeStartX = 0;
      this.swipeStartY = 0;
    },
    goToReview(index) {
      const sortedReviews = [...this.reviewInfos].sort((a, b) => 
        parseFloat(b.average || 0) - parseFloat(a.average || 0)
      );
      const maxIndex = Math.min(sortedReviews.length, 5) - 1;
      this.currentReviewIndex = Math.max(0, Math.min(index, maxIndex));
    },
    // Swipe functionality for photos carousel
    handlePhotoSwipeStart(e) {
      if (this.isGuest) return;
      if (e.touches) {
        this.swipeStartX = e.touches[0].clientX;
        this.swipeStartY = e.touches[0].clientY;
      } else {
        this.swipeStartX = e.clientX;
        this.swipeStartY = e.clientY;
      }
    },
    handlePhotoSwipeEnd(e) {
      if (this.isGuest) return;
      if (!this.swipeStartX || !this.allShowimages.length) {
        this.swipeStartX = 0;
        this.swipeStartY = 0;
        return;
      }
      
      let endX, endY;
      if (e.changedTouches) {
        endX = e.changedTouches[0].clientX;
        endY = e.changedTouches[0].clientY;
      } else {
        endX = e.clientX;
        endY = e.clientY;
      }
      
      const diffX = this.swipeStartX - endX;
      const diffY = this.swipeStartY - endY;
      
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > this.swipeThreshold) {
        this.isSwiping = true;
        if (diffX > 0) {
          this.currentImageIndex = Math.min(this.currentImageIndex + 1, this.allShowimages.length - 1);
        } else {
          this.currentImageIndex = Math.max(this.currentImageIndex - 1, 0);
        }
        
        if (e.preventDefault) e.preventDefault();
        setTimeout(() => { this.isSwiping = false; }, 300);
      } else {
        this.isSwiping = false;
      }
      
      this.swipeStartX = 0;
      this.swipeStartY = 0;
    },
    goToImage(index) {
      if (this.isGuest) return;
      if (this.allShowimages.length) {
        this.currentImageIndex = Math.max(0, Math.min(index, this.allShowimages.length - 1));
      }
    },
    resolveCommentId(comment) {
      if (!comment || typeof comment !== "object") {
        return null;
      }
      const candidateKeys = ["id", "commentId", "commentID", "comment_id", "commentid"];
      for (const key of candidateKeys) {
        const value = comment[key];
        if (value !== undefined && value !== null && value !== "") {
          return value;
        }
      }
      return null;
    },
    handleDeleteComment(comment) {
      const commentId = this.resolveCommentId(comment);
      if (!commentId) {
        Swal.fire({
          icon: "error",
          title: "Unable to delete comment",
          text: "Missing comment identifier. Please refresh and try again.",
          confirmButtonColor: '#3CC2FF'
        });
        return;
      }
      this.confirmDeleteComment(commentId);
    },
    getCommentAvatar(comment) {
      if (!comment || typeof comment !== "object") {
        return "/avatar/1.png";
      }
      const candidateKeys = ["avatar", "avatarUrl", "profilePhoto", "profileImage", "headImg", "headImage", "iconUrl"];
      for (const key of candidateKeys) {
        const value = comment[key];
        if (typeof value === "string" && value.trim() !== "") {
          return value;
        }
      }
      return "/avatar/1.png";
    },
    getCommentSchool(comment) {
      if (!comment || typeof comment !== "object") {
        return "";
      }
      const candidateKeys = ["school", "schoolName", "university", "universityName", "college", "organization"];
      for (const key of candidateKeys) {
        const value = comment[key];
        if (typeof value === "string" && value.trim() !== "") {
          return value;
        }
      }
      return "";
    },
  reviewInfo(houseId,houseLetId,obj){
    axios.get('/api/house/reviewInfo', {
      params: {
        'houseId': houseId,
        'houseLetId': houseLetId,
      },
      timeout: 10000
    })
    .then(function (response) {
      console.log("reviewInfos")
      obj.reviewInfos = response.data.data || [];
      console.log(obj.reviewInfos)
      obj.statusList = new Array(obj.reviewInfos.length).fill(false);
      obj.calculateRatings();
      obj.allImages();
      console.log("allshow")
      console.log(obj.allShowimages)
      obj.calculateStatsFromReviews();
      obj.currentReviewIndex = 0;
      obj.currentImageIndex = 0;
    })
    .catch(function (error) {
      console.log(error)
    });
  },
  getallinfo(houseId,houseLetId,obj){
    console.log("allinfo")
    console.log(houseId)
    console.log(houseLetId)
    axios.get('/api/house/allinfo', {
      params: {
        'houseId': houseId,
        'houseLetId': houseLetId,
      },
      timeout: 10000
    })
    .then(function (response) {
      console.log("allinfo response:", response)
      const data = response.data.data || response.data;
      obj.allinfo = data || {};
      
      if (!obj.allinfo.safety) obj.allinfo.safety = 0;
      if (!obj.allinfo.costTransparency) obj.allinfo.costTransparency = 0;
      if (!obj.allinfo.location) obj.allinfo.location = 0;
      if (!obj.allinfo.listingAccuracy) obj.allinfo.listingAccuracy = 0;
      if (!obj.allinfo.cleanliness) obj.allinfo.cleanliness = 0;
      if (!obj.allinfo.internetReliability) obj.allinfo.internetReliability = 0;
      if (!obj.allinfo.noiseLevel) obj.allinfo.noiseLevel = 0;
      if (!obj.allinfo.roomSize) obj.allinfo.roomSize = 0;
      if (!obj.allinfo.maintenance) obj.allinfo.maintenance = 0;
      if (!obj.allinfo.livingComfort) obj.allinfo.livingComfort = 0;
      if (!obj.allinfo.average) obj.allinfo.average = 0;
      
      if (data && data.lat) {
        obj.maplocation.lat = data.lat;
      }
      if (data && data.lon) {
        obj.maplocation.lng = data.lon;
      }
      console.log("allinfo processed:", obj.allinfo)
      console.log(obj.maplocation)
    })
    .catch(function (error) {
      console.error("Error fetching allinfo:", error)
      obj.allinfo = {
        safety: 0,
        costTransparency: 0,
        location: 0,
        listingAccuracy: 0,
        cleanliness: 0,
        internetReliability: 0,
        noiseLevel: 0,
        roomSize: 0,
        maintenance: 0,
        livingComfort: 0,
        average: 0
      };
    });
  },
  getHouseLetInfoNow(houletid){
    this.getHouseLetInfoNowImpl(this,houletid);
  },
  getHouseLetInfoNowImpl(obj,houletid){
    console.log("发送给后端search")
    axios.get('/api/house/let', {
      params: {
        houseletId: houletid,
      },
      timeout: 10000
    })
    .then(function (response) {
      console.log(obj.searchQuery)
      obj.searchResults1 = response.data.data != null ? response.data.data : {};
      console.log(obj.searchResults1)
      obj.searchQuery2 = (obj.searchResults1 && obj.searchResults1.houseLetName) || '';
      console.log(obj.searchQuery2)
    })
    .catch(function (error) {
      console.log(error)
      obj.searchResults1 = {};
      obj.showDropdown = true;
    });
  },

  getHouseLetInfo(houid){
    this.getHouseLetInfoImpl(this,houid);
  },
  getHouseLetInfoImpl(obj,houid){
    console.log("发送给后端search")
    axios.get('/api/search/room', {
      params: {
        houseId: houid,
        pageNo: 1,
        pageSize: 10
      },
      timeout: 10000
    })
    .then(function (response) {
      console.log(obj.searchQuery)
      obj.searchResults2 = response.data.data || [];
      console.log(obj.searchResults2)
      if(obj.houseLetId ==null){
        console.log("进来了")
        if(obj.searchResults2 && obj.searchResults2.length > 0){
          obj.$router.push({ path: '/HouseReviewScore', query: { houseId: obj.houseId,houseLetId: obj.searchResults2[0].id } });
        }else{
          console.warn('No rooms found for this house');
        }
      }else{
        obj.getHouseLetInfoNow(obj.houseLetId);
      }
      // After rooms are loaded, fetch reviews from ALL rooms
      obj.fetchAllRoomReviews();
    })
    .catch(function (error) {
      console.log(error)
      obj.searchResults2 = [];
    });
  },
  fetchHouseInfo(houseId){
      this.getHouseInfo(this,houseId)
  },
  getHouseInfo(obj,house){
    axios.get('/api/house', {
      params: {
        houseId: house,
      },
      timeout: 10000
    })
    .then(function (response) {
      console.log("obj.res")
      obj.res = response.data.data != null ? response.data.data : {}
      console.log(response.data.data);
      obj.count=10
      obj.fetchSimilarHouses();
    })
    .catch(function (error) {
      console.log(error);
      obj.res = {};
    });
  },
//   ToRegistInfo(){
//     if (this.$route.path !== '/rateTheHouse') {
//       this.$router.push({ path: '/rateTheHouse', query: { houseId: this.houseId,houseLetId: this.houseLetId } });
//   }
//   // 假设你从后端获取的 JWT 如下
// const jwt = localStorage.getItem('token');
// if (jwt) {
//       // 分割 JWT 获取载荷部分
//       const payloadBase64Url = jwt.split('.')[1];

//       // 替换 URL 安全的 Base64 编码字符为标准 Base64 编码字符
//       const payloadBase64 = payloadBase64Url.replace(/-/g, '+').replace(/_/g, '/');

//       // 补充 Base64 编码需要的填充字符
//       const payloadBase64Padded = payloadBase64 + '='.repeat((4 - payloadBase64.length % 4) % 4);

//       // 解码 Base64 字符串为 JSON 字符串
//       const payloadJson = atob(payloadBase64Padded);

//       // 将 JSON 字符串解析为 JavaScript 对象
//       const payload = JSON.parse(payloadJson);

//       console.log(payload); // 打印载荷部分的内容
//       this.userName = payload.name
//       console.log(this.userName)
//     }
    


// },
  load () {
    if(this.count<100)
    this.count += 1
  },
  handleCommand(command) {
  this.selectedRoom = command; // 更新选中的房间名称
  },
    handleSelectChange2(value) {
      if (value === 0) {
        // "All Rooms" selected
        this.reviewFilter = 'all';
        this.selectedRoom = 0;
        // Keep the current houseLetId for allinfo display
        return;
      }
      if (!value) {
        return;
      }
      this.reviewFilter = 'current';
      this.houseLetId = value;
      this.selectedRoom = Number(value);
      this.$router.replace({ 
        path: '/HouseReviewScore', 
        query: { houseId: this.houseId, houseLetId: value } 
      });
      this.getallinfo(this.houseId, value, this);
      this.reviewInfo(this.houseId, value, this);
      this.getHouseLetInfoNow(value);
      this.fetchRoomLikeStatus();
    },
    toggleReviewFilter() {
      if (this.reviewFilter === 'all') {
        this.reviewFilter = 'current';
      } else {
        this.reviewFilter = 'all';
      }
    },
    goToHouse(houseId) {
      this.$router.push({ path: '/HouseReviewScore', query: { houseId } });
    },
    fetchSimilarHouses() {
      const district = this.res && this.res.district;
      if (!district) return;
      const currentId = this.houseId;
      axios.get('/api/search/autocomplete', { params: { keyword: district, limit: 10 } })
        .then(response => {
          const houses = (response.data.data || []).filter(h => h.id != currentId);
          // Pull house-level aggregate (weighted rating + total reviews across rooms).
          const aggPromises = houses.map(h =>
            axios.get(`/api/house/${h.id}/aggregate`)
              .then(r => {
                const d = r.data && r.data.data;
                return {
                  ...h,
                  avg: d && d.averageRating ? d.averageRating : null,
                  reviewCount: d && d.reviewCount ? d.reviewCount : 0,
                };
              })
              .catch(() => ({ ...h, avg: null, reviewCount: 0 }))
          );
          Promise.all(aggPromises).then(results => {
            this.similarHouses = results.slice(0, 5);
          });
        })
        .catch(() => {});
    },
    fetchAllRoomReviews() {
      if (!this.searchResults2 || !this.searchResults2.length) {
        // No rooms loaded yet, just use current reviews
        this.allRoomReviews = [...this.reviewInfos];
        return;
      }
      const obj = this;
      const promises = this.searchResults2.map(room => {
        return axios.get('/api/house/reviewInfo', {
          params: {
            houseId: obj.houseId,
            houseLetId: room.id,
          },
          timeout: 10000
        }).then(response => {
          const reviews = response.data.data || [];
          // Tag each review with its room name for display
          return reviews.map(r => ({
            ...r,
            _roomName: room.houseLetName || ('Room ' + room.id),
            _roomId: room.id
          }));
        }).catch(() => []);
      });
      Promise.all(promises).then(results => {
        const merged = results.flat();
        // Deduplicate by review id
        const seen = new Set();
        const unique = [];
        merged.forEach(r => {
          const key = r.id || (r.name + r.content);
          if (!seen.has(key)) {
            seen.add(key);
            unique.push(r);
          }
        });
        // Sort by date descending
        unique.sort((a, b) => {
          const dateA = Array.isArray(a.dateTime) ? new Date(a.dateTime[0], a.dateTime[1]-1, a.dateTime[2]) : new Date(0);
          const dateB = Array.isArray(b.dateTime) ? new Date(b.dateTime[0], b.dateTime[1]-1, b.dateTime[2]) : new Date(0);
          return dateB - dateA;
        });
        obj.allRoomReviews = unique;
        console.log(`Fetched ${unique.length} reviews across ${obj.searchResults2.length} rooms`);
      });
    },
     fixImageUrl(url) {
      if (!url || url === "null") {
        return "/reviewimages/null.png";
      }
      return url;
    },
  prehandle2(query){
        this.handleSearch2(this,query)
      },
      handleSearch2(obj,query) {
      console.log("发送给后端search")
      console.log(query)
      console.log(this.selectedId)
      // 调用后端接口进行搜索
      axios.get('/api/search/room', {
          params: {
              keyword: query,
              houseId: this.selectedId,
              pageNo: 1,
              pageSize: 10
          }
      })
      .then(function (response) {
        console.log(obj.searchQuery)

        obj.searchResults2 = response.data.data != null ? response.data.data : []; // 假设后端返回的是一个数组
        obj.showDropdown = true;
        console.log(obj.searchResults2)
      })
      .catch(function (error) {
        console.log(error)
        obj.searchResults2 = []; // 清空下拉选项
        obj.showDropdown = true;
      });
    },getClass(value) {
      return value > 3 ? 'number1' : 'number';
    },
  getClass2(value) {
      return value == 1 ? 'show_green' : 'show_little_green';
    },
  calculateRatings() {
      this.amazingCount = 0;
      this.goodCount = 0;
      this.averageCount = 0;
      this.poorCount = 0;
      this.awfulCount = 0;
      
      this.reviewInfos.forEach(review => {
        const avg = parseFloat(review.average) || 0;
        if (avg >= 4.5) this.amazingCount++;
        else if (avg >= 3.5) this.goodCount++;
        else if (avg >= 2.5) this.averageCount++;
        else if (avg >= 1.5) this.poorCount++;
        else this.awfulCount++;
      });
    },
    calculateStatsFromReviews() {
      if (!this.reviewInfos || this.reviewInfos.length === 0) {
        if (!this.allinfo.average) this.allinfo.average = 0;
        return;
      }
      
      const stats = {
        safety: 0,
        costTransparency: 0,
        location: 0,
        listingAccuracy: 0,
        cleanliness: 0,
        internetReliability: 0,
        noiseLevel: 0,
        roomSize: 0,
        maintenance: 0,
        livingComfort: 0,
        average: 0
      };
      
      let count = 0;
      this.reviewInfos.forEach(review => {
        if (review.safety != null) stats.safety += parseFloat(review.safety) || 0;
        if (review.costTransparency != null) stats.costTransparency += parseFloat(review.costTransparency) || 0;
        if (review.location != null) stats.location += parseFloat(review.location) || 0;
        if (review.listingAccuracy != null) stats.listingAccuracy += parseFloat(review.listingAccuracy) || 0;
        if (review.cleanliness != null) stats.cleanliness += parseFloat(review.cleanliness) || 0;
        if (review.internetReliability != null) stats.internetReliability += parseFloat(review.internetReliability) || 0;
        if (review.noiseLevel != null) stats.noiseLevel += parseFloat(review.noiseLevel) || 0;
        if (review.roomSize != null) stats.roomSize += parseFloat(review.roomSize) || 0;
        if (review.maintenance != null) stats.maintenance += parseFloat(review.maintenance) || 0;
        if (review.livingComfort != null) stats.livingComfort += parseFloat(review.livingComfort) || 0;
        if (review.average != null) stats.average += parseFloat(review.average) || 0;
        count++;
      });
      
      if (count > 0) {
        Object.keys(stats).forEach(key => {
          stats[key] = stats[key] / count;
        });
        this.allinfo = { ...this.allinfo, ...stats };
        console.log('Recalculated ratings from reviews:', stats);
      }
    },
// handleLike() {
//       console.log("Like button clicked");
//     },
    handleUnlike() {
      console.log("Unlike button clicked");
    },
    handleComment() {
      console.log("Comment button clicked");
    },

  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

/* ========== GUEST BLUR + LOGIN CTA ========== */
.guest-blur {
  filter: blur(7px);
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
}
.guest-blur * {
  pointer-events: none !important;
}

.guest-login-cta {
  position: sticky;
  top: 56px;
  z-index: 50;
  display: flex;
  justify-content: center;
  padding: 10px 16px 0;
}
.guest-login-cta-inner {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 720px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  border-radius: 999px;
  box-shadow: 0 10px 30px rgba(99, 102, 241, 0.35);
  font-size: 0.92rem;
}
.guest-login-cta-icon { font-size: 1.05rem; }
.guest-login-cta-text { flex: 1; line-height: 1.35; }
.guest-login-cta-btn {
  background: #fff;
  color: #4f46e5;
  border: none;
  border-radius: 999px;
  padding: 6px 16px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.guest-login-cta-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0,0,0,0.18);
}
@media (max-width: 480px) {
  .guest-login-cta { top: 48px; padding: 8px 8px 0; }
  .guest-login-cta-inner { padding: 8px 10px; font-size: 0.82rem; gap: 8px; border-radius: 14px; }
  .guest-login-cta-btn { padding: 5px 12px; font-size: 0.82rem; }
}

/* ========== BASE STYLES ========== */
.house-review-page {
  margin-top: 0;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  background: #f8fafc; /* Light background */
  min-height: 100vh;
  color: #1f2937;

}
.star-container{
  height: 100px;
  weight:100px;
}

/* ========== 3D BACKGROUND ========== */
.bg-3d-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}
.floating-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.15;
  animation: float-orb 20s ease-in-out infinite;
}
.orb-1 { width: 400px; height: 400px; background: #c7d2fe; top: -100px; left: -100px; }
.orb-2 { width: 350px; height: 350px; background: #818cf8; bottom: -100px; right: -100px; animation-delay: -7s; }
.orb-3 { width: 300px; height: 300px; background: #a5b4fc; top: 50%; left: 50%; animation-delay: -14s; }
@keyframes float-orb {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

/* ========== GLASS CARD ========== */
.glass-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
}

/* ========== HERO SECTION ========== */
.hero-section {
  position: relative;
  z-index: 1;
  padding: 12px 24px 0;
}
.hero-container {
  max-width: 1280px;
  margin: 0 auto;
}
.hero-card {
  display: grid;
  grid-template-columns: 1fr 1.1fr 1fr;
  gap: 2rem;
  padding: 1.5rem 2rem 2rem;
}

/* ===== HERO LEFT — Rating, Title, Actions ===== */
.hero-left {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.rating-block {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.rating-summary {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
}
.mobile-right-stack {
  display: contents;
}
.rating-main {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.avg-rent-line {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #3CC2FF;
}
.rating-big {
  font-size: 4rem;
  font-weight: 800;
  line-height: 1;
}
.rating-big.rating-green { color: #16a34a; }
.rating-big.rating-yellow { color: #ca8a04; }
.rating-big.rating-red { color: #dc2626; }
.rating-big.rating-not-reviewed {
  font-size: 1.5rem;
  font-weight: 600;
  color: #9ca3af;
}
.rating-max {
  font-size: 1.1rem;
  color: #9ca3af;
  font-weight: 500;
}
.reviews-count {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
}
.reviews-count strong {
  color: #3CC2FF;
}
.property-name {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0.25rem 0 0.15rem 0;
  line-height: 1.3;
}
.property-address {
  font-size: 0.92rem;
  color: #6b7280;
  margin: 0 0 0.25rem 0;
  line-height: 1.4;
}
.managed-by {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}
.managed-by-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f7fb0;
  background: #e8f9ff;
  padding: 2px 10px;
  border-radius: 6px;
}
.mobile-price-block,
.mobile-like-btn,
.mobile-cta-block {
  display: none;
}
/* compound selector beats the .students-say-section { display: flex } below */
.students-say-section.mobile-students-say {
  display: none;
}
.mobile-price-block {
  align-items: baseline;
  gap: 0.25rem;
}
.mobile-price-value {
  font-size: 1.8rem;
  font-weight: 800;
  color: #f59e0b;
  line-height: 1;
}
.mobile-price-unit {
  font-size: 0.82rem;
  font-weight: 700;
  color: #f59e0b;
}
.mobile-cta-block {
  flex-direction: column;
  gap: 0.75rem;
}
.mobile-rate-btn {
  width: 100%;
  justify-content: center;
}

/* Action Buttons Row */
.action-buttons-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.25rem;
}
.heart-btn {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: 1.5px solid #e5e7eb;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}
.heart-btn svg { width: 22px; height: 22px; }
.heart-btn:hover { border-color: #ef4444; background: #fef2f2; }
.heart-btn.active { border-color: #ef4444; background: #fef2f2; }

.rate-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.4rem;
  background: linear-gradient(135deg, #3CC2FF, #1eaeea);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s;
}
.rate-btn svg { width: 18px; height: 18px; }
.rate-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(60, 194, 255, 0.4); }

/* Room Selector */
.room-selector-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
}
.room-selector-styled {
  min-width: 160px;
}
.add-room-btn {
  border-radius: 8px !important;
  font-weight: 600 !important;
}

/* ===== HERO MIDDLE — Stats & Comments ===== */
.hero-middle {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.stats-card {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.stats-empty {
  text-align: center;
  padding: 1.5rem 0;
  color: #9ca3af;
  font-weight: 600;
  font-size: 1.05rem;
}
.stats-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.stat-block {
  padding: 1rem;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}
.stat-block.top { border-left: 3px solid #22c55e; }
.stat-block.low { border-left: 3px solid #f59e0b; }
.stat-block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}
.stat-block-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.stat-block-score {
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 6px;
  padding: 2px 8px;
}
.stat-block-score.excellent { background: #dcfce7; color: #166534; }
.stat-block-score.warning { background: #fef3c7; color: #92400e; }
.stat-block-category {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.stat-block-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3CC2FF;
}
.stat-block-icon svg { width: 22px; height: 22px; }
.stat-block-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
}

/* Expanded Stats Grid */
.stats-expanded {
  padding-top: 0.5rem;
  border-top: 1px solid #e2e8f0;
}
.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}
.stat-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  transition: background 0.15s;
}
.stat-row:hover { background: #f1f5f9; }
.stat-icon {
  width: 24px;
  height: 24px;
  color: #3CC2FF;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stat-icon svg { width: 20px; height: 20px; }
.stat-label {
  flex: 1;
  font-size: 0.82rem;
  color: #4b5563;
  font-weight: 500;
}
.stat-value {
  font-size: 0.85rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  min-width: 32px;
  text-align: center;
}

/* Show All Button */
.show-all-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.6rem;
  background: transparent;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  color: #3CC2FF;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.show-all-btn svg {
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
}
.show-all-btn svg.rotated { transform: rotate(180deg); }
.show-all-btn:hover { background: #f1f5f9; border-color: #3CC2FF; }

/* Student Comments */
.students-say-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.section-label {
  font-size: 0.7rem;
  font-weight: 700;
  color: #9ca3af;
  letter-spacing: 1px;
  margin: 0;
}
.student-comment-card {
  padding: 1rem 1.25rem;
  background: #f8fafc;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  cursor: grab;
  user-select: none;
}
.comment-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.comment-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
}
.comment-school {
  font-size: 0.75rem;
  color: #0f7fb0;
  font-weight: 600;
  background: #e8f9ff;
  padding: 2px 8px;
  border-radius: 4px;
}
.student-comment-card .comment-text {
  font-size: 0.85rem;
  color: #374151;
  line-height: 1.5;
  margin: 0;
}
.comment-dots {
  display: flex;
  gap: 6px;
  justify-content: center;
  margin-top: 0.75rem;
}
.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #d1d5db;
  cursor: pointer;
  transition: all 0.2s;
}
.dot.active {
  background: #3CC2FF;
  transform: scale(1.3);
}

/* ===== HERO RIGHT — Photos & Attributes ===== */
.hero-right {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.photo-carousel {
  position: relative;
}
.photo-main {
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 16px;
  overflow: hidden;
  background: #f1f5f9;
  cursor: pointer;
}
.main-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.photo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  gap: 0.5rem;
}
.photo-placeholder svg { width: 48px; height: 48px; }
.photo-placeholder span { font-size: 0.85rem; }
.photo-dots {
  display: flex;
  gap: 6px;
  justify-content: center;
  margin-top: 0.75rem;
}
.photo-dots .dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #d1d5db;
  cursor: pointer;
  transition: all 0.2s;
}
.photo-dots .dot.active {
  background: #3CC2FF;
  transform: scale(1.3);
}

/* Attributes Grid */
.attributes-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
}
.attr-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0.6rem 0.75rem;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}
.attr-label {
  font-size: 0.7rem;
  color: #9ca3af;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.attr-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2937;
}
.attr-value.pill {
  display: inline-block;
  width: fit-content;
  padding: 2px 10px;
  background: #e8f9ff;
  color: #0f7fb0;
  border-radius: 20px;
  font-size: 0.78rem;
}
.company-link {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
}
.company-link a {
  color: #3CC2FF;
  font-weight: 600;
  text-decoration: none;
}
.company-link a:hover {
  text-decoration: underline;
}
.review-actions-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 12px 0;
}
.copy-info-btn {
  padding: 8px 14px;
  background: #3CC2FF;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.copy-info-btn:hover {
  background: #1eaeea;
}
.copy-info-btn:active {
  transform: scale(0.98);
}

/* ========== CONTENT SECTIONS WRAPPER ========== */
.content-sections-wrapper {
  position: relative;
  z-index: 1;
  padding: 2rem;
}
.content-section {
  max-width: 1280px;
  margin: 0 auto;
}
.content-container {
  width: 100%;
}
.two-column-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

/* Address Card */
.address-card {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.address-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.address-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}
.address-text {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
}
.address-detail {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
}
.map-container {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  margin-top: 0.5rem;
  position: relative;
}
.map-view {
  width: 100%;
  height: 280px;
  z-index: 1;
}
.map-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.95rem;
  z-index: 2;
  pointer-events: none;
}

/* Right Column */
.right-column {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.tags-card, .distribution-card, .similar-card {
  padding: 1.5rem;
}
.tags-card h3, .distribution-card h3, .similar-card h3 {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1rem;
}
.distribution-empty {
  text-align: center;
  padding: 1rem 0;
  color: #9ca3af;
  font-weight: 600;
}
.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.tag {
  padding: 0.4rem 0.9rem;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 500;
  color: white;
}
.tag.purple { background: #3CC2FF; }
.tag.cyan { background: #06b6d4; }
.tag.blue { background: #3CC2FF; }
.tag.green { background: #22c55e; }
.tag.pink { background: #ec4899; }
.tag.orange { background: #f97316; }

/* Distribution */
.distribution-bars {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}
.dist-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.dist-label {
  min-width: 85px;
  font-size: 0.82rem;
  color: #6b7280;
  font-weight: 500;
}
.dist-bar {
  flex: 1;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}
.dist-fill {
  height: 100%;
  background: linear-gradient(90deg, #3CC2FF, #1eaeea);
  border-radius: 4px;
  transition: width 0.4s ease;
}
.dist-count {
  min-width: 24px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
  text-align: right;
}

/* Similar Houses */
.similar-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.similar-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s;
}
.similar-item:hover { background: #eef2ff; border-color: #c7d2fe; }
.similar-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #e0f2fe;
  color: #0369a1;
  flex: 0 0 auto;
}
.similar-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #374151;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.similar-meta {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: #64748b;
  flex: 0 0 auto;
}
.similar-rating-text {
  font-weight: 600;
  color: #b45309;
}
.similar-review-count {
  color: #94a3b8;
}

/* ========== REVIEWS SECTION (updated) ========== */
.reviews-section {
  position: relative;
  z-index: 1;
  max-width: 1280px;
  margin: 0 auto 3rem;
  padding: 0 2rem;
}
.reviews-section > .content-container {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  padding: 2rem;
}
.reviews-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 0.75rem;
  position: relative;
  z-index: 3;
}
.reviews-header h2 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}
.reviews-header-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.review-search-bar {
  display: flex;
  align-items: center;
  background: #f1f5f9;
  border: 1.5px solid transparent;
  border-radius: 12px;
  padding: 0.45rem 0.85rem;
  transition: all 0.25s ease;
  min-width: 220px;
  max-width: 340px;
  position: relative;
  z-index: 5;
  pointer-events: auto;
}
.review-search-bar:focus-within {
  border-color: #3CC2FF;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(60, 194, 255, 0.15);
}
.review-search-icon {
  flex-shrink: 0;
  margin-right: 0.5rem;
}
.review-search-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.85rem;
  font-weight: 500;
  color: #1f2937;
  width: 100%;
  font-family: inherit;
  pointer-events: auto;
  -webkit-user-select: text;
  user-select: text;
  cursor: text;
}
.review-search-input::placeholder {
  color: #94a3b8;
  font-weight: 400;
}
.review-search-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e2e8f0;
  border: none;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  cursor: pointer;
  padding: 0;
  margin-left: 0.4rem;
  flex-shrink: 0;
  transition: background 0.2s;
}
.review-search-clear:hover {
  background: #cbd5e1;
}
.no-search-results {
  text-align: center;
  color: #64748b;
  font-size: 0.95rem;
  padding: 2rem 1rem;
  margin: 0;
}
.review-sort-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  position: relative;
  z-index: 2;
}
.sort-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.9rem;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: inherit;
  color: #475569;
  background: #f1f5f9;
  border: 1.5px solid transparent;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  position: relative;
  z-index: 2;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.sort-pill:hover {
  background: #e2e8f0;
  color: #1e293b;
}
.sort-pill.active {
  background: #e8f9ff;
  color: #0f7fb0;
  border-color: #3CC2FF;
  box-shadow: 0 0 0 2px rgba(60, 194, 255, 0.12);
}
.room-filter {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  background: #f1f5f9;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;
}
.room-filter:hover { background: #e2e8f0; }
.filter-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
}

.reviews-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.reviews-list .review-card {
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
}
.review-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}
.review-rating {
  font-size: 1.1rem;
  font-weight: 700;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  min-width: 44px;
  text-align: center;
  flex-shrink: 0;
}
.reviewer-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}
.reviewer-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.reviewer-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.reviewer-name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #1f2937;
}
.reviewer-school {
  font-size: 0.8rem;
  color: #3CC2FF;
  font-weight: 500;
}
.review-date {
  font-size: 0.8rem;
  color: #9ca3af;
}
.review-actions-top {
  display: flex;
  gap: 0.4rem;
  flex-shrink: 0;
}
.review-actions-top .action-btn {
  padding: 0.35rem 0.75rem;
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s;
}
.action-btn.edit {
  background: #e8f9ff;
  color: #0f7fb0;
}
.action-btn.edit:hover { background: #d7f5ff; }
.action-btn.delete {
  background: #fef2f2;
  color: #dc2626;
}
.action-btn.delete:hover { background: #fecaca; }
.admin-delete-block { margin-top: 10px; display: flex; gap: 8px; flex-wrap: wrap; }
.admin-edit-btn {
  background: #eff6ff;
  color: #2563eb;
  border: 1.5px solid #93c5fd;
  border-radius: 8px;
  padding: 6px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.admin-edit-btn:hover { background: #dbeafe; }
.admin-delete-btn {
  background: #fef2f2;
  color: #dc2626;
  border: 1.5px solid #fca5a5;
  border-radius: 8px;
  padding: 6px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.admin-delete-btn:hover { background: #fecaca; }
.admin-edit-panel {
  margin-top: 14px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px 20px;
}
.admin-edit-title { font-size: 1rem; font-weight: 700; color: #1e293b; margin: 0 0 12px; }
.admin-edit-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 16px;
  margin-bottom: 12px;
}
.admin-edit-grid label {
  display: flex;
  flex-direction: column;
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
  gap: 4px;
}
.admin-edit-grid label.admin-edit-full { grid-column: 1 / -1; }
.admin-edit-grid input {
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.88rem;
  color: #1e293b;
  background: #fff;
  outline: none;
}
.admin-edit-grid input:focus { border-color: #3bc2ff; }
.admin-save-btn {
  background: #3bc2ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 7px 20px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.admin-save-btn:hover { background: #1aabef; }
.admin-save-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.admin-room-row { border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; margin-bottom: 12px; background: #fff; }
.admin-room-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.admin-room-label { font-size: 0.9rem; font-weight: 600; color: #334155; }
.admin-room-delete-btn {
  background: #fef2f2; color: #dc2626; border: 1px solid #fca5a5;
  border-radius: 6px; padding: 4px 12px; font-size: 0.8rem; font-weight: 600; cursor: pointer;
}
.admin-room-delete-btn:hover { background: #fecaca; }
.admin-no-rooms { color: #94a3b8; font-size: 0.88rem; margin: 8px 0 0; }
.action-btn.view {
  background: #f0fdf4;
  color: #16a34a;
}
.action-btn.view:hover { background: #bbf7d0; }

/* Review Badges */
.review-badges {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.badge {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}
.badge.yourself {
  background: #dcfce7;
  color: #166534;
}
.badge.yourself.small {
  font-size: 0.65rem;
  padding: 1px 6px;
}
.badge.recommended {
  background: #dcfce7;
  color: #166534;
  cursor: pointer;
}
.badge.room-badge {
  background: #e8f9ff;
  color: #0f7fb0;
  border: 1px solid #b7ecff;
}

.review-text {
  font-size: 0.9rem;
  color: #374151;
  line-height: 1.6;
  margin: 0 0 0.75rem;
}

.translated-text-block {
  margin-bottom: 0.75rem;
}

.translation-toggle {
  border: 1px solid #bae6fd;
  border-radius: 999px;
  background: #f0f9ff;
  color: #0284c7;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 5px 10px;
}

.translation-toggle:disabled {
  cursor: wait;
  opacity: 0.7;
}

.comment-translation-toggle {
  margin-top: 4px;
  padding: 3px 8px;
}

.review-details-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 0.75rem;
  padding: 10px 12px;
  background: linear-gradient(135deg, #e8f9ff 0%, #f2fcff 100%);
  border-radius: 10px;
  border: 1px solid #cfefff;
}
.detail-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(255,255,255,0.85);
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 500;
  color: #334155;
  border: 1px solid #e2e8f0;
  white-space: nowrap;
}
.detail-icon {
  font-size: 0.85rem;
}
.detail-value {
  color: #1e293b;
}
.review-insight {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 0.5rem;
  padding: 8px 12px;
  background: #fef3c7;
  border-radius: 8px;
  border-left: 3px solid #f59e0b;
  font-size: 0.82rem;
}
.insight-label {
  font-weight: 600;
  color: #92400e;
  white-space: nowrap;
}
.insight-text {
  color: #78350f;
  line-height: 1.4;
}
.review-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 0.75rem;
}
.review-tag {
  padding: 3px 10px;
  border-radius: 999px;
  background: #e0e7ff;
  color: #4338ca;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #a5b4fc;
}

.review-images {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}
.review-image {
  width: 80px;
  height: 80px;
  border-radius: 10px;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.2s;
}
.review-image:hover { transform: scale(1.05); }

/* Review Footer */
.review-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f5f9;
}
.helpful-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.helpful-label {
  font-size: 0.8rem;
  color: #9ca3af;
  font-weight: 500;
}
.helpful-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.35rem 0.75rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.2s;
}
.helpful-btn:hover { background: #e8f9ff; border-color: #b7ecff; }
.comment-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.35rem 0.75rem;
  background: #e8f9ff;
  border: 1px solid #b7ecff;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f7fb0;
  cursor: pointer;
  transition: all 0.2s;
}
.comment-toggle:hover { background: #d7f5ff; }

/* Comments Section */
.comments-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}
.comments-section h4 {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.75rem;
}
.comments-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.comment-item {
  display: flex;
  gap: 0.65rem;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 12px;
}
.comment-avatar-small {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.comment-content {
  flex: 1;
  min-width: 0;
}
.comment-meta {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.comment-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2937;
}
.comments-section .comment-school {
  font-size: 0.75rem;
  color: #3CC2FF;
}
.comment-date {
  font-size: 0.75rem;
  color: #9ca3af;
  display: block;
  margin-top: 1px;
}
.comment-body {
  font-size: 0.85rem;
  color: #374151;
  margin: 0.25rem 0 0;
  line-height: 1.5;
}
.delete-comment {
  background: none;
  border: none;
  color: #ef4444;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 0;
  margin-top: 4px;
}
.delete-comment:hover { text-decoration: underline; }

.comment-form {
  display: flex;
  gap: 0.5rem;
}
.comment-form textarea {
  flex: 1;
  padding: 0.6rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.85rem;
  resize: vertical;
  min-height: 40px;
  font-family: inherit;
  transition: border-color 0.2s;
}
.comment-form textarea:focus {
  outline: none;
  border-color: #3CC2FF;
}
.submit-comment {
  padding: 0.6rem 1.2rem;
  background: #3CC2FF;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;
}
.submit-comment:hover { background: #1eaeea; }

/* ========== RESPONSIVE — TABLET ========== */
@media (max-width: 1024px) {
  .hero-card {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
  }
  .hero-left { grid-column: 1 / 2; }
  .hero-middle { grid-column: 2 / 3; }
  .hero-right { grid-column: 1 / -1; }
  .two-column-layout { grid-template-columns: 1fr; }
}

/* ========== RESPONSIVE — MOBILE ========== */
@media (max-width: 768px) {
  .house-review-page {
    background: #f3f4f6;
    margin-top: 0;
  }
  .bg-3d-container {
    display: none;
  }
  .hero-section { padding: 0.4rem 0.75rem 0; }
  .property-address { display: none !important; }
  .avg-rent-line { display: none; }
  .hero-card {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.85rem;
    border-radius: 18px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
  }
  /* Flatten the desktop columns so leaf children participate in hero-card flex */
  .hero-left, .hero-middle, .hero-right {
    display: contents;
  }
  /* Mobile vertical order matches target screens 1 → 2 → 3 */
  .rating-block            { order: 1; }
  .property-title-block    { order: 2; }
  .managed-by              { order: 3; }
  .room-selector-section   { order: 4; }
  .photo-carousel          { order: 5; }
  .mobile-cta-block        { order: 6; }
  .mobile-students-say     { order: 7; }
  .review-actions-bar      { order: 8; }
  .attributes-grid         { order: 9; }
  .stats-card              { order: 10; }
  /* Mobile-redundant elements (mobile-cta-block replaces them) */
  .hero-left > .reviews-count { display: none; }
  .action-buttons-row      { display: none; }
  .rating-block {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem 0.6rem;
    width: 100%;
    margin-bottom: 0.05rem;
  }
  .rating-summary {
    flex: 0 0 auto;
    flex-direction: row;
    align-items: center;
    gap: 0.35rem;
  }
  .rating-main {
    flex-shrink: 0;
    align-items: baseline;
  }
  /* Let the three children of mobile-right-stack flow as siblings of rating-summary */
  .mobile-right-stack {
    display: contents;
  }
  .rating-big {
    font-size: 2.4rem;
    font-weight: 800;
    letter-spacing: -0.04em;
  }
  .rating-max { font-size: 0.9rem; color: #6b7280; }
  .verification-badge {
    flex: 1 1 auto;
    min-width: 0;
    gap: 0.3rem;
    padding: 0;
    justify-content: flex-start;
    width: auto;
    max-width: none;
  }
  .verification-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
  .verification-text {
    font-size: 0.82rem;
    color: #6b7280;
    line-height: 1.2;
    text-align: left;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: 0.25rem;
    max-width: 100%;
  }
  .verification-status {
    color: #9ca3af;
    font-weight: 700;
  }
  .verification-badge.verified .verification-status {
    color: #16a34a;
  }
  .mobile-price-block {
    display: flex;
    align-items: baseline;
    gap: 0.2rem;
    justify-content: flex-end;
    flex-basis: 100%;
    order: 3;
    margin-top: -0.15rem;
  }
  .mobile-price-value {
    font-size: 1.5rem;
    font-weight: 800;
    color: #f9ca5f;
    line-height: 1;
  }
  .mobile-price-unit {
    font-size: 0.78rem;
    font-weight: 700;
    color: #f9ca5f;
  }
  .property-name {
    font-size: 1.55rem;
    margin-top: 0.55rem;
    margin-bottom: 0.1rem;
    line-height: 1.1;
  }
  .managed-by { margin: 0; }
  .property-address { font-size: 0.95rem; color: #374151; margin-bottom: 0.45rem; }
  .managed-by-name {
    background: #e0f7ff;
    color: #0284c7;
    border-radius: 999px;
    font-size: 0.72rem;
    padding: 3px 9px;
  }
  .hero-left .reviews-count {
    display: none;
  }
  .action-buttons-row {
    margin-top: 0;
    justify-content: flex-end;
  }
  .desktop-like-btn {
    display: none;
  }
  .mobile-like-btn {
    display: inline-flex;
    align-self: center;
    order: 2;
    margin-left: auto;
    flex: 0 0 auto;
  }
  .action-buttons-row .rate-btn {
    display: none;
  }
  .heart-btn {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    border-color: #d1d5db;
    background: #fff;
  }
  .heart-btn svg {
    width: 20px;
    height: 20px;
  }
  .room-selector-section {
    margin-top: 0.1rem;
    display: block;
  }
  .room-selector-styled {
    width: 100%;
  }
  .add-room-btn {
    display: none !important;
  }
  .room-selector-styled :deep(.el-input__inner) {
    height: 42px;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
    font-size: 0.98rem;
    color: #111827;
    padding: 0 14px;
  }
  .photo-main {
    aspect-ratio: 16 / 9;
    max-height: 220px;
    border-radius: 14px;
    border: 1px solid #e5e7eb;
  }
  .mobile-cta-block {
    display: block;
    text-align: center;
    margin-top: 0;
  }
  .mobile-reviews-count {
    font-size: 0.78rem;
    color: #6b7280;
    margin: 0 0 0.4rem 0;
  }
  .mobile-rate-btn {
    width: 100%;
    justify-content: center;
    background: linear-gradient(90deg, #3bc2ff, #0284c7);
    border-radius: 10px;
    padding: 0.55rem 1rem;
    font-size: 0.92rem;
    font-weight: 700;
    box-shadow: none;
  }
  .stats-card,
  .attributes-grid,
  .review-actions-bar {
    display: flex;
  }
  .stats-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
    border-radius: 14px;
    padding: 0.8rem;
    gap: 0.8rem;
  }
  .stats-summary {
    grid-template-columns: 1fr;
    gap: 0.55rem;
  }
  .stat-block {
    padding: 0.7rem;
    border-radius: 10px;
  }
  .show-all-btn {
    min-height: 36px;
    border-radius: 8px;
  }
  .attributes-grid {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 0;
    margin-top: 0.15rem;
  }
  .attr-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.2rem;
    padding: 0.75rem 0.95rem;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
  .attr-label {
    text-transform: uppercase;
    font-size: 0.65rem;
    letter-spacing: 0.6px;
    color: #9ca3af;
    font-weight: 700;
  }
  .attr-value {
    font-size: 0.95rem;
    color: #1f2937;
    font-weight: 600;
    background: transparent;
    padding: 0;
  }
  .attr-value.pill {
    align-self: flex-start;
    background: #e0f2fe;
    color: #0284c7;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    font-size: 0.82rem;
    font-weight: 700;
  }
  .review-actions-bar {
    margin-top: 0.55rem;
    padding: 0.7rem 0 0;
    border-top: 1px dashed #e5e7eb;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
  }
  .company-link {
    font-size: 0.85rem;
    color: #4b5563;
    margin: 0;
    font-weight: 500;
  }
  .copy-info-btn {
    border-radius: 8px;
    min-height: 34px;
    padding: 0.45rem 0.7rem;
    font-size: 0.74rem;
    flex-shrink: 0;
  }
  .students-say-section {
    margin-top: 0.3rem;
    padding-top: 0.5rem;
    border-top: 1px dashed #e5e7eb;
  }
  .student-comment-card {
    padding: 0.65rem 0.8rem !important;
  }
  .student-comment-card .comment-text {
    font-size: 0.85rem;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .students-say-section.desktop-students-say {
    display: none;
  }
  .students-say-section.mobile-students-say {
    display: flex;
  }
  .section-label {
    font-size: 0.72rem;
    letter-spacing: 0.8px;
    color: #9ca3af;
    margin-bottom: 0.2rem;
  }
  .student-comment-card {
    border-radius: 12px;
    background: #f8fafc;
    border-color: #e5e7eb;
    padding: 0.8rem 0.95rem;
  }
  .comment-header {
    margin-bottom: 0.4rem;
  }
  .comment-school {
    background: #e0f7ff;
    color: #0284c7;
    border-radius: 999px;
    font-size: 0.72rem;
  }
  .student-comment-card .comment-text {
    font-size: 0.9rem;
    line-height: 1.42;
  }
  .comment-dots {
    margin-top: 0.6rem;
  }
  .photo-dots .dot,
  .comment-dots .dot {
    width: 6px;
    height: 6px;
  }
  .stats-grid { grid-template-columns: 1fr; }
  .content-sections-wrapper {
    padding: 0.7rem;
  }
  .content-section {
    max-width: 100%;
  }
  .content-container {
    border-radius: 14px;
  }
  .two-column-layout {
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
  }
  /* Flatten right-column so all four bottom cards order as siblings */
  .right-column {
    display: contents;
  }
  .address-card,
  .tags-card,
  .distribution-card,
  .similar-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
    padding: 0.85rem;
  }
  .tags-card {
    order: 1;
  }
  .address-card {
    order: 2;
  }
  .similar-card {
    order: 3;
  }
  .distribution-card {
    order: 4;
  }
  .address-title,
  .tags-card h3,
  .distribution-card h3,
  .similar-card h3 {
    font-size: 0.95rem;
    margin-bottom: 0.55rem;
  }
  .address-text {
    font-size: 0.8rem;
  }
  .address-detail {
    font-size: 0.78rem;
  }
  .map-view {
    height: 185px;
  }
  .tag {
    font-size: 0.72rem;
    border-radius: 999px;
    padding: 0.3rem 0.58rem;
  }
  .dist-label,
  .dist-count {
    font-size: 0.74rem;
  }
  .similar-item {
    padding: 0.6rem;
    border-radius: 8px;
  }
  .similar-rating {
    font-size: 0.75rem;
    padding: 3px 8px;
  }
  .similar-name {
    font-size: 0.8rem;
  }
  .reviews-section {
    display: block;
    margin: 0 auto;
    padding: 0 0.7rem 0.7rem;
    background: transparent;
    box-shadow: none;
    border-radius: 0;
  }
  .reviews-section > .content-container {
    padding: 1rem;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
  .reviews-header { flex-direction: column; align-items: flex-start; }
  .reviews-header-controls { width: 100%; }
  .review-search-bar { min-width: 0; max-width: none; flex: 1; }
  .review-header { flex-direction: column; }
  .review-actions-top { align-self: flex-start; }
}

@media (max-width: 480px) {
  .hero-section { padding: 0.35rem 0.55rem 0; }
  .hero-card { padding: 0.75rem; gap: 0.45rem; border-radius: 14px; }
  .action-buttons-row { flex-direction: row; align-items: center; justify-content: flex-end; }
  .rate-btn { justify-content: center; }
  .room-selector-section { flex-direction: column; }
  .room-selector-styled { width: 100%; }
  .attributes-grid { grid-template-columns: 1fr; }
  .reviews-section { padding: 0 0.55rem 0.55rem; }
  .reviews-section > .content-container { padding: 0.85rem; }
  .reviews-header-controls { flex-direction: column; }
  .review-search-bar { width: 100%; max-width: none; }
  .review-sort-filters { gap: 0.35rem; }
  .sort-pill { font-size: 0.75rem; padding: 0.35rem 0.7rem; }

  /* Additional mobile fixes */
  .rating-big { font-size: 2.1rem !important; }
  .rating-max { font-size: 0.8rem !important; }
  .mobile-right-stack {
    max-width: none;
  }
  .property-name { font-size: 1.4rem !important; padding: 0 !important; margin-top: 0.4rem !important; }
  .property-address { font-size: 0.88rem !important; padding: 0 !important; margin-bottom: 0.45rem !important; }
  .managed-by { font-size: 0.8rem !important; }
  .heart-btn {
    width: 36px;
    height: 36px;
  }
  .mobile-price-value {
    font-size: 1.3rem;
  }
  .mobile-price-unit {
    font-size: 0.72rem;
  }
  .photo-main {
    border-radius: 12px;
    max-height: 190px;
  }
  .mobile-reviews-count {
    font-size: 0.84rem;
  }
  .mobile-rate-btn {
    padding: 0.62rem 0.85rem;
    font-size: 0.9rem;
  }
  .stats-card {
    padding: 0.7rem;
  }
  .stat-block {
    padding: 0.62rem;
  }
  .stat-label,
  .stat-value {
    font-size: 0.75rem;
  }
  .content-sections-wrapper {
    padding: 0.5rem;
  }
  .address-card,
  .tags-card,
  .distribution-card,
  .similar-card {
    padding: 0.72rem;
    border-radius: 12px;
  }
  .map-view {
    height: 165px;
  }
  .tag {
    font-size: 0.68rem;
  }
  .dist-row {
    gap: 0.45rem;
  }
  .dist-bar {
    height: 7px;
  }
  .go-now-btn {
    min-height: 30px;
    padding: 0.26rem 0.56rem;
    font-size: 0.69rem;
  }
  .student-comment-card .comment-text {
    font-size: 0.86rem;
  }

  /* Modal and dialog responsive */
  .el-dialog {
    width: 95vw !important;
    max-width: 95vw !important;
    margin: 0 auto !important;
  }

  .el-dialog__header {
    padding: 14px 16px !important;
  }

  .el-dialog__title {
    font-size: 1rem !important;
  }

  .el-dialog__body {
    padding: 14px 16px !important;
  }

  /* Popup forms responsive */
  .el-input__inner,
  .el-select .el-input__inner,
  .el-textarea__inner {
    padding: 8px 10px !important;
    font-size: 14px !important;
    border-radius: 6px !important;
  }

  /* Button sizing */
  .el-button {
    padding: 8px 12px !important;
    font-size: 13px !important;
    border-radius: 6px !important;
  }

  /* Text area responsive */
  .el-textarea__inner {
    min-height: 80px !important;
    font-size: 14px !important;
  }

  /* Form section responsive */
  .el-form-item {
    margin-bottom: 12px !important;
  }

  .el-form-item__label {
    font-size: 13px !important;
  }
}

/* Main Content Container */
.main-content-container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  padding: 2rem;
  min-height: 60vh;
  align-items: start;
}

/* Left Content Area */
.left-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 0;
}

.overall-rating-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1rem;
}

.rating-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.rating-display-large {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.rating-number-large {
  font-size: 5rem;
  font-weight: 800;
  color: #000000; /* Black color */
}

.rating-max-large {
  font-size: 1.2rem;
  color: #000000; /* Black color */
  vertical-align: top;
  line-height: 0;
  position: relative;
  top: -1.5em;
}

.rating-description {
  color: #6b7280; /* Grey text */
  font-size: 1rem;
  font-weight: 500;
}

/* Verification Badge */
.verification-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  background: transparent;
  border-radius: 20px;
  border: none;
}

.verification-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.verification-icon1 {
  width: 36px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.verification-text {
  font-size: 0.85rem;
  font-weight: 500;
  color: #3CC2FF;
  line-height: 1.2;
  text-align: left;
}

.room-selector {
  margin-bottom: 0.5rem;
}

.room-dropdown {
  width: 100%;
}

.room-dropdown :deep(.el-input__wrapper) {
  background: white;
  border: none;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.room-dropdown :deep(.el-input__inner) {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
}

.property-info {
  display: flex;
  margin-bottom: 2rem;
}
.icon1{
  background-color: transparent; /* 设置背景颜色为透明 */
}
.property-title {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;       /* 调整行间距，防止重叠 */
  color: #1f2937;
  margin-bottom: 20px;  /* 调整h1标签之间的间距 */
}

.property-description {
  color: #6b7280; /* Grey text */
  line-height: 1.6;
  font-size: 1rem;
}

.rate-button {
  width: 100%;
  background: #3CC2FF !important;
  border: none !important;
  padding: 1rem !important;
  font-size: 1.1rem !important;
  font-weight: 600 !important;
  border-radius: 12px !important;
  color: white !important;
}

.rate-button:hover {
  background: #1eaeea !important;
}


.company-info {
  margin-left: 30%;
  color: #6b7280; /* Grey text */
  font-style: italic;
  font-size: 0.9rem;
  margin-top: 1rem;
  text-align: center !important;
  padding: 0.5rem;
  line-height: 2rem; /* 设置行高与容器高度一致 */
  height: 2rem; /* 设置容器高度 */
}

/* Discover Section */
.discover-section {
  margin-bottom: 2rem;
}
.star-button{
  background-color: transparent;
  border: transparent;
  margin-left: 50px;
}
.discover-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
  position: relative;
}

.discover-title::after {
  content: '';
  position: absolute;
  bottom: -0.5rem;
  left: 0;
  width: 40px;
  height: 3px;
  background: #3CC2FF;
  border-radius: 2px;
}

.discover-photos {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  margin-top: 1rem;
}

.photo-item {
  position: relative;
  flex-shrink: 0;
  width: 125px;
  height: 150px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease;
  background: #f3f4f6;
}

.photo-item:hover {
  transform: scale(1.05);
}

.discover-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.yes_center_mid{
  background-color: white;
  border-radius: 20px;
}
.photo-overlay {
  position: relative;
}

.photo-overlay-text {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: 600;
}

/* Property Info Row */
.property-info-row {
  display: flex;
  flex-direction: row !important;
  gap: 2rem;
  margin-bottom: 2rem;
  width: 100%;
  max-width: 100%;
  margin-left: 0;
  margin-right: 0;
}

/* Property Attributes Section */
.property-attributes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding: 2rem;
  background: rgba(238, 242, 255, 1);
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  width: 50%;
  flex: 1;
  min-height: 200px;
}


.property-attributes1 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding: 2rem;
  background: rgba(238, 242, 255, 1);
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  width: 100%;
  flex: 1;
  min-height: 200px;
}

.attribute-item {

  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: center;
}


.attribute-label {
  font-size: 1rem;
  color: #0f7fb0;
  font-weight: 600;
}

.attribute-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f7fb0;
  padding: 0.75rem 1.5rem;
  background: white;
  border-radius: 25px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Address Section */
.address-section {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 2rem;
  background: rgba(238, 242, 255, 1);
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  width: 50%;
  flex: 1;
  min-height: 200px;
}
.map1{
  height: 390px;
  width: 270px;
}
.out-map-placeholder{
  width: 300px;
}
.address-info {
  flex: 1;
  margin-right: 1rem;
}

.address-label {
  font-size: 1rem;
  color: #3CC2FF;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.address-text {
  font-size: 0.9rem;
  font-weight: bold;
  color: #374151;
  line-height: 1.4;
}

.address-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
}
.out-address-actions{
  display: flex;
  background: rgba(238, 242, 255, 1);
}
.go-now-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #3CC2FF;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.go-now-btn:hover {
  background: #1eaeea;
}

.go-now-btn svg {
  width: 16px;
  height: 16px;
}

.map-placeholder {
  margin-top: 13px;
  width: 274px;
  height: 394px;
  background: #f3f4f6;
  border: 2px dashed #d1d5db;
}

/* Right Content Area */
.right-content {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding-top: 0;
}

.rating-breakdown-card {
  background: rgba(238, 242, 255, 1);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.rating-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(5, 1fr);
  gap: 0.75rem;
  align-items: start;
}

.rating-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: transparent;
  border-radius: 8px;
  border: none;
  min-height: 60px;
}

.rating-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3CC2FF;
  flex-shrink: 0;
}

.rating-icon.room-size {
  color: #3CC2FF;
}

.rating-icon svg {
  width: 40px;
  height: 40px;
}

.rating-info {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
}

.rating-label {
  font-size: 0.85rem;
  color: #6b7280;
  font-weight: 500;
  flex-shrink: 0;
}

.rating-score {
  font-size: 1rem;
  font-weight: 700;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  width: fit-content;
  color: #1f2937;
  min-width: 35px;
  text-align: center;
}

.score-excellent {
  background: #dcfce7;
  color: #166534;
}

.score-good {
  background: #fef3c7;
  color: #92400e;
}

.score-poor {
  background: #fee2e2;
  color: #991b1b;
}

/* Middle Section */
.middle-section {
  max-width: 1200px;
  margin: 2rem auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  padding: 0 2rem;
}

.middle-left-column {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.middle-right-column {

  display: flex;
  flex-direction: column;
}


.top-tags-card,
.rating-distribution-card,
.similar-houses-card {
  height: 400px;
  background: rgba(238, 242, 255, 1);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.top-tags-card h3,
.rating-distribution-card h3,
.similar-houses-card h3 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag {
  background: #3CC2FF;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

.tag1 {
  background: #0cb405; /* Blue tags */
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

.tag11 {
  background: yellow; /* Blue tags */
  color: black;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

.distribution-bars {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.distribution-item {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.distribution-item .rating-label {
  min-width: 80px;
  font-size: 0.9rem;
  color: #6b7280; /* Grey text */
}

.bar-container {
  flex: 1;
  height: 8px;
  background: #e5e7eb; /* Light bar background */
  border-radius: 4px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: #3CC2FF;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.rating-count {
  min-width: 30px;
  text-align: right;
  font-size: 0.9rem;
  color: #6b7280; /* Grey text */
  font-weight: 500;
}

.similar-houses {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.similar-house {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb; /* Light background */
  border-radius: 8px;
}

.house-rating {
  background: #3CC2FF;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
}

.house-name {
  font-weight: 500;
  color: #1f2937;
}

.reviews-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 2rem;
  text-align: center;
}

.reviews-list {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.review-card {
  background: rgba(238, 242, 255, 1);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
}
.company-link {
  color: #3CC2FF;
  text-decoration: none;
  font-style: inherit;
}

.company-link:hover {
  text-decoration: underline; /* 鼠标悬停时显示下划线 */
}
.review-header {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.review-rating-large {
  background: #fef3c7; /* Yellow background */
  color: #92400e;
  padding: 1rem;
  border-radius: 12px;
  min-width: 60px;
  text-align: center;

  justify-content: center; /* 水平居中 */
  align-items: center;     /* 垂直居中 */
}

.rating-number {
  font-size: 1.5rem;
  font-weight: 700;
}

.reviewer-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1rem;
}
.yourself-info{
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1rem;
  
}
.reviewer-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e7eb;
}

.reviewer-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.reviewer-details {
  flex: 1;
}

.reviewer-name {
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.review-date {
  font-size: 0.9rem;
  color: #6b7280; /* Grey text */
}

.review-content {
  margin-bottom: 1.5rem;
}

.review-text {
  color: #374151; /* Dark grey text */
  line-height: 1.6;
  margin-bottom: 1rem;
}

.review-images {
  margin-top: 1rem;
}

.image-gallery {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.review-image {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}

.review-image:hover {
  transform: scale(1.05);
}

.review-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}
.review-details-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 0.75rem;
  padding: 8px 10px;
  background: linear-gradient(135deg, #e8f9ff 0%, #f2fcff 100%);
  border-radius: 10px;
  border: 1px solid #cfefff;
}
.review-insight {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 0.5rem;
  padding: 8px 10px;
  background: #fef3c7;
  border-radius: 8px;
  border-left: 3px solid #f59e0b;
  font-size: 0.8rem;
}

.review-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.helpful-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.helpful-text {
  font-size: 0.9rem;
  color: #6b7280; /* Grey text */
}

.action-btn {
  background: none;
  border: 1px solid #d1d5db;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  color: #374151;
}

.action-btn:hover {
  background: #f9fafb;
  border-color: #3CC2FF;
}

.bookmark-btn {
  background: none;
  border: 1px solid #d1d5db;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1.2rem;
  color: #374151;
}

.bookmark-btn:hover {
  background: #f9fafb;
  border-color: #3CC2FF;
}

/* Image Overlay */
.image-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.enlarged-image {
  max-width: 90%;
  max-height: 90%;
  border-radius: 8px;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
}

.close-button {
  position: absolute;
  top: 20px;
  right: 30px;
  font-size: 2rem;
  color: white;
  cursor: pointer;
  background: none;
  border: none;
  font-weight: bold;
}

/* Desktop - Hide mobile button */
.rate-button-mobile {
  display: none;
}

.rate-button-desktop {
  display: inline-flex;
}

.property-title-wrapper {
  display: block;
}

/* Hide show all ratings button on desktop */
.show-all-ratings-btn {
  display: none;
}
</style>








<style>
.avatar-middle {
  width: 25px;
  height: 25px;
  object-fit: cover;
  cursor: pointer;
  border-radius: 6px;
  transition: transform 0.2s ease;
}
.avatar-middle:hover {
  transform: scale(1.05);
}
.image-container_1 {
  display: inline-block;
  margin: 5px;
}
.review-form_1 {
  max-width: 100%;
  margin: 30px auto;
  padding: 30px 40px;
  background-color: #f5f7fa;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.review-form_1 h2 {
  font-size: 26px;
  margin-bottom: 10px;
  color: #222;
  font-weight: 600;
}

.review-form_1 textarea {
  width: 100%;
  min-height: 140px;
  padding: 18px 20px;
  border: 2px solid #a0aec0;
  border-radius: 15px;
  resize: vertical;
  font-size: 16px;
  font-family: inherit;
  transition: border-color 0.3s, box-shadow 0.3s;
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.06);
  margin-bottom: 0;
}

.review-form_1 textarea:focus {
  border-color: #3CC2FF;
  outline: none;
  box-shadow: 0 0 8px rgba(99, 102, 241, 0.5);
}

.submit-button {
  align-self: center;
  background: linear-gradient(135deg, #3CC2FF, #1eaeea);
  color: white;
  font-size: 17px;
  font-weight: 600;
  padding: 12px 32px;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  box-shadow: 0 6px 14px rgba(99, 102, 241, 0.4);
  transition: background 0.4s ease;
}

.submit-button:hover {
  background: linear-gradient(135deg, #1eaeea, #1597cc);
  box-shadow: 0 8px 18px rgba(60, 194, 255, 0.6);
}

.reviews-list1 {
  margin-top: 30px;
  text-align: center;
}

.reviews-list1 h2 {
  font-size: 24px;
  margin-bottom: 25px;
  color: #2d3748;
  font-weight: 600;
}

.review-item {
  background-color: #ffffff;
  padding: 20px 25px;
  margin-bottom: 18px;
  border-radius: 16px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.07);
  text-align: left;
  transition: box-shadow 0.3s ease;
}

.review-item:hover {
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
}

.review-item p {
  margin: 8px 0;
  font-size: 15.5px;
  color: #4a5568;
  line-height: 1.4;
}

.review-item strong {
  color: #1a202c;
}
</style>
